import { useState, useRef, useEffect, useCallback } from "react";
import { initializeApp } from "firebase/app";
import { getAuth, signInWithEmailAndPassword, signOut, onAuthStateChanged, updatePassword } from "firebase/auth";
import { getFirestore, collection, doc, onSnapshot, setDoc, updateDoc, addDoc, deleteDoc, getDoc, serverTimestamp, query, orderBy } from "firebase/firestore";
import { getMessaging, getToken, onMessage } from "firebase/messaging";

// ─── FIREBASE ─────────────────────────────────────────────────────────────────
const firebaseConfig = {
  apiKey: "AIzaSyATuF4teM8VSRCT0-wKb1XT3TJSLSTT0oQ",
  authDomain: "rt30x6.firebaseapp.com",
  projectId: "rt30x6",
  storageBucket: "rt30x6.firebasestorage.app",
  messagingSenderId: "902307168178",
  appId: "1:902307168178:web:f5c99f126d452c3c02aeab",
  measurementId: "G-TVS4HHLV3D"
};
const fbApp  = initializeApp(firebaseConfig);
const auth   = getAuth(fbApp);
const db     = getFirestore(fbApp);

const VAPID_KEY = "BPj5bqr1K0W_y1hBYVXG3YT6lC_IslenaN8vxUsxFREuC-TSAelfSyOAcXOFd3FJ4UZBJdweSwK4UmRy5KxZ_2g";

// ─── PUSH NOTIFICATIONS ───────────────────────────────────────────────────────
let messaging = null;
try { messaging = getMessaging(fbApp); } catch(e) { /* not supported in all browsers */ }

async function requestPushPermission() {
  if (!messaging) return null;
  try {
    const permission = await Notification.requestPermission();
    if (permission !== "granted") return null;
    const token = await getToken(messaging, { vapidKey: VAPID_KEY });
    return token;
  } catch(e) { return null; }
}

async function savePushToken(memberId, token) {
  if (!token) return;
  await updateDoc(doc(db, "members", String(memberId)), { pushToken: token });
}

// Show a local notification (works when app is open)
function showLocalNotification(title, body) {
  if (Notification.permission === "granted") {
    new Notification(title, { body, icon: "/logo192.png" });
  }
}

// Check birthdays and show notification
function checkBirthdayNotifications(members, shownRef) {
  const now = new Date();
  const today = `${String(now.getMonth()+1).padStart(2,"0")}-${String(now.getDate()).padStart(2,"0")}`;
  for (const m of members) {
    if (!m.birthday) continue;
    const bdMD = m.birthday.slice(5); // "MM-DD"
    const key = `bday-${m.id}-${now.getFullYear()}`;
    if (bdMD === today && !shownRef.current[key]) {
      showLocalNotification("🎂 Tillykke med fødselsdagen!", `${m.name} har fødselsdag i dag!`);
      shownRef.current[key] = true;
      localStorage.setItem(key, "1");
    }
  }
}
function checkAutoNotifications(meetings, shownRef) {
  const now = new Date();
  now.setHours(0,0,0,0);
  for (const m of meetings) {
    if (!isUpcoming(m.date)) continue;
    const meetingDate = new Date(m.date);
    const daysLeft = Math.ceil((meetingDate - now) / 86400000);
    const key7   = `notif-7-${m.id}`;
    const keyToday = `notif-today-${m.id}`;
    const keyDl  = `notif-dl-${m.id}`;

    // 7 days before
    if (daysLeft === 7 && !shownRef.current[key7]) {
      showLocalNotification("RT30X6 – Møde om 7 dage", `${m.title} – ${fmtShort(m.date)} kl. ${m.time}`);
      shownRef.current[key7] = true;
      localStorage.setItem(key7, "1");
    }
    // Day of meeting
    if (daysLeft === 0 && !shownRef.current[keyToday]) {
      showLocalNotification("RT30X6 – Møde i aften!", `${m.title} kl. ${m.time} på ${m.location}`);
      shownRef.current[keyToday] = true;
      localStorage.setItem(keyToday, "1");
    }
    // Deadline tomorrow
    if (m.deadline) {
      const dlDate = new Date(m.deadline);
      const dlDays = Math.ceil((dlDate - now) / 86400000);
      if (dlDays === 1 && !shownRef.current[keyDl]) {
        showLocalNotification("RT30X6 – Husk tilmelding!", `Frist i morgen for ${m.title}`);
        shownRef.current[keyDl] = true;
        localStorage.setItem(keyDl, "1");
      }
    }
  }
}

// ─── PAGE TITLE ───────────────────────────────────────────────────────────────
document.title = "RT30X6";

// ─── FONTS ────────────────────────────────────────────────────────────────────
const _fl = document.createElement("link");
_fl.rel = "stylesheet";
_fl.href = "https://fonts.googleapis.com/css2?family=Outfit:wght@400;500;600;700&display=swap";
document.head.appendChild(_fl);

// ─── PALETTE ─────────────────────────────────────────────────────────────────
const DARK = {
  bg:       "#08090b",
  surface:  "#0f1215",
  card:     "#13171c",
  border:   "#1f252d",
  borderHi: "#2c3540",
  gold:     "#c8a84b",
  goldDim:  "#7a6428",
  white:    "#f0ece4",
  soft:     "#9aa4ae",
  faint:    "#3a4550",
  green:    "#4ade80",
  yellow:   "#facc15",
  red:      "#f87171",
  r: 16, rs: 10,
};
const LIGHT = {
  bg:       "#f5f5f0",
  surface:  "#ffffff",
  card:     "#fafaf7",
  border:   "#e0ddd5",
  borderHi: "#c8c4b8",
  gold:     "#9a7530",
  goldDim:  "#c8a84b",
  white:    "#1a1a1a",
  soft:     "#555550",
  faint:    "#aaa9a0",
  green:    "#22a855",
  yellow:   "#d4a017",
  red:      "#dc3545",
  r: 16, rs: 10,
};
let C = DARK; // default, overridden by App based on user preference

const pctColor = (pct) => pct >= 70 ? C.green : pct >= 51 ? C.yellow : C.red;

// ─── CONSTANTS ────────────────────────────────────────────────────────────────
const CLUB_ROLES = ["Formand", "Næstformand", "Revisor", "Kasserer", "Inspektør", "Medlem", "Orlov"];
const SYS_ROLES  = ["admin", "member"]; // system: admin = kan oprette/slette

// ─── DATA ─────────────────────────────────────────────────────────────────────
const TODAY      = new Date();
TODAY.setHours(0,0,0,0);
const isUpcoming = (d) => new Date(d) >= TODAY;
const isPast     = (d) => !isUpcoming(d);
const daysUntil  = (d) => Math.ceil((new Date(d) - TODAY) / 86400000);
const getMember  = (members, id) => members.find(m => m.id === id);
const initials   = (name) => name.split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase();

const fmtLong  = (d) => new Date(d).toLocaleDateString("da-DK", { weekday: "long", day: "numeric", month: "long" });
const fmtFull  = (d) => new Date(d).toLocaleDateString("da-DK", { day: "numeric", month: "long", year: "numeric" });
const fmtShort = (d) => new Date(d).toLocaleDateString("da-DK", { day: "numeric", month: "short", year: "numeric" });

const CY_START = (() => {
  const d = TODAY;
  return d.getMonth() >= 4 ? new Date(d.getFullYear(), 4, 1) : new Date(d.getFullYear() - 1, 4, 1);
})();

const pastCYMeetings = (meetings) => meetings.filter(m => isPast(m.date) && new Date(m.date) >= CY_START && !m.noAttendance && !m.isBoardMeeting);
const memberAttPct   = (memberId, meetings) => {
  const rel = pastCYMeetings(meetings);
  if (!rel.length) return null;
  return Math.round((rel.filter(m => m.attendees.includes(memberId)).length / rel.length) * 100);
};
const meetingAttPct = (meeting, total) =>
  total === 0 ? 0 : Math.round((meeting.attendees.length / total) * 100);

// ─── SEED MEMBERS ────────────────────────────────────────────────────────────
const INIT_MEMBERS = [
  { id: 1,  name: "Morten Bøjland",         email: "boejland@gmail.com", phone: "28250180", address: "", birthday: "", sysRole: "admin",  clubRole: "Medlem", board: false, initials: "MB", password: "demo123", mustChangePw: false },
  { id: 2,  name: "Ulrik Walter Rasmussen",  email: "uwr@live.dk",        phone: "40681555", address: "", birthday: "", sysRole: "admin",  clubRole: "Medlem", board: false, initials: "UW", password: "demo123", mustChangePw: false },
  { id: 3,  name: "Henrik Høegh",            email: "", phone: "", address: "", birthday: "", sysRole: "member", clubRole: "Formand",  board: false, initials: "HH", password: "rt30x6!", mustChangePw: true },
  { id: 4,  name: "Peter Følbæk Nielsen",    email: "", phone: "", address: "", birthday: "", sysRole: "member", clubRole: "Kasserer", board: false, initials: "PF", password: "rt30x6!", mustChangePw: true },
  { id: 5,  name: "Peter Lindholm Sørensen", email: "", phone: "", address: "", birthday: "", sysRole: "member", clubRole: "Medlem",   board: false, initials: "PL", password: "rt30x6!", mustChangePw: true },
  { id: 6,  name: "Bo Forsberg",             email: "", phone: "", address: "", birthday: "", sysRole: "member", clubRole: "Medlem",   board: false, initials: "BF", password: "rt30x6!", mustChangePw: true },
  { id: 7,  name: "Henrik Ulsø",             email: "", phone: "", address: "", birthday: "", sysRole: "member", clubRole: "Medlem",   board: false, initials: "HU", password: "rt30x6!", mustChangePw: true },
  { id: 8,  name: "Christian Jensen",        email: "", phone: "", address: "", birthday: "", sysRole: "member", clubRole: "Medlem",   board: false, initials: "CJ", password: "rt30x6!", mustChangePw: true },
  { id: 9,  name: "Henrik Frickmann",        email: "", phone: "", address: "", birthday: "", sysRole: "member", clubRole: "Medlem",   board: false, initials: "HF", password: "rt30x6!", mustChangePw: true },
  { id: 10, name: "Anders Nyberg",           email: "", phone: "", address: "", birthday: "", sysRole: "member", clubRole: "Medlem",   board: false, initials: "AN", password: "rt30x6!", mustChangePw: true },
  { id: 11, name: "Lars Asmussen",           email: "", phone: "", address: "", birthday: "", sysRole: "member", clubRole: "Medlem",   board: false, initials: "LA", password: "rt30x6!", mustChangePw: true },
  { id: 12, name: "Lars Fendt-Hansen",       email: "", phone: "", address: "", birthday: "", sysRole: "member", clubRole: "Medlem",   board: false, initials: "LF", password: "rt30x6!", mustChangePw: true },
  { id: 13, name: "Lars Skibsted",           email: "", phone: "", address: "", birthday: "", sysRole: "member", clubRole: "Medlem",   board: false, initials: "LS", password: "rt30x6!", mustChangePw: true },
  { id: 14, name: "Leif Olsen",              email: "", phone: "", address: "", birthday: "", sysRole: "member", clubRole: "Medlem",   board: false, initials: "LO", password: "rt30x6!", mustChangePw: true },
  { id: 15, name: "Kåre Schneekloth",        email: "", phone: "", address: "", birthday: "", sysRole: "member", clubRole: "Medlem",   board: false, initials: "KS", password: "rt30x6!", mustChangePw: true },
  { id: 16, name: "Anders Bytoft",           email: "", phone: "", address: "", birthday: "", sysRole: "member", clubRole: "Medlem",   board: false, initials: "AB", password: "rt30x6!", mustChangePw: true },
];

// Member ID reference:
// 1=Morten, 2=Ulrik, 3=Henrik Høegh, 4=Peter Følbæk, 5=Peter Lindholm
// 6=Bo, 7=Henrik Ulsø, 8=Christian, 9=Henrik Frickmann, 10=Anders Nyberg
// 11=Lars Asmussen, 12=Lars Fendt-Hansen, 13=Lars Skibsted, 14=Leif, 15=Kåre, 16=Anders Bytoft

// Attendance key: 1=deltog, A=afbud
// Møde 1  (13.05.25): AN=A, AB=1, BF=1, CJ=A, HH=1, HU=A, HF=1, KS=1, LA=A, LF=1, LS=1, LO=1, MB=1, PF=1, PL=1, UW=1
// Møde 2  (13.06.25): AN=A, AB=A, BF=A, CJ=1, HH=1, HU=A, HF=1, KS=1, LA=1, LF=1, LS=1, LO=1, MB=A, PF=1, PL=A, UW=1
// Møde 3  (26.08.25): AN=1, AB=1, BF=A, CJ=1, HH=1, HU=1, HF=1, KS=1, LA=A, LF=1, LS=1, LO=1, MB=1, PF=1, PL=A, UW=A
// Møde 4  (23.09.25): AN=1, AB=A, BF=A, CJ=1, HH=1, HU=A, HF=1, KS=1, LA=1, LF=A, LS=1, LO=1, MB=1, PF=1, PL=1, UW=1
// Møde 5  (21.10.25): AN=A, AB=A, BF=A, CJ=A, HH=1, HU=A, HF=1, KS=1, LA=A, LF=A, LS=A, LO=A, MB=1, PF=1, PL=1, UW=1
// Møde 6  (11.11.25): AN=A, AB=1, BF=A, CJ=A, HH=1, HU=A, HF=1, KS=1, LA=1, LF=1, LS=1, LO=A, MB=1, PF=A, PL=A, UW=A
// Møde 7  (09.12.25): AN=A, AB=1, BF=A, CJ=1, HH=1, HU=A, HF=1, KS=1, LA=1, LF=1, LS=1, LO=1, MB=1, PF=1, PL=1, UW=1
// Møde 8  (23.01.26): AN=A, AB=1, BF=A, CJ=A, HH=1, HU=A, HF=1, KS=1, LA=1, LF=1, LS=1, LO=A, MB=1, PF=1, PL=A, UW=1
// Møde 9  (17.02.26): AN=A, AB=A, BF=A, CJ=1, HH=1, HU=1, HF=1, KS=1, LA=1, LF=A, LS=1, LO=1, MB=1, PF=1, PL=1, UW=1

const INIT_MEETINGS = [
  {
    id: 101, title: "Generalforsamling", subtitle: "Maj 2025",
    date: "2025-05-13", time: "18:30", location: "Kirppu HQ, Gefionsvej 6, 3400 Hillerød",
    description: "", agenda: "", deadline: "",
    responsible1: null, responsible2: null, boedemester: null, treMinutter: null,
    attendees: [16,6,3,9,15,11,12,13,14,1,4,5,2],
    absent:    [10,7,8],
    fines: [], documents: [], rsvpLog: [],
  },
  {
    id: 102, title: "Sommerhus", subtitle: "Juni 2025",
    date: "2025-06-13", time: "15:00", location: "Tisvilde",
    description: "", agenda: "", deadline: "",
    responsible1: null, responsible2: null, boedemester: null, treMinutter: null,
    attendees: [8,3,9,15,11,12,13,14,4,2],
    absent:    [10,16,6,7,1,5],
    fines: [], documents: [], rsvpLog: [],
  },
  {
    id: 103, title: "Opstartsmøde", subtitle: "August 2025",
    date: "2025-08-26", time: "18:30", location: "Arc Nordic, Helsingørsgade, 3400 Hillerød",
    description: "", agenda: "", deadline: "",
    responsible1: null, responsible2: null, boedemester: null, treMinutter: null,
    attendees: [10,16,8,3,7,9,15,12,13,14,1,4],
    absent:    [6,11,5,2],
    fines: [], documents: [], rsvpLog: [],
  },
  {
    id: 104, title: "AI møde", subtitle: "September 2025",
    date: "2025-09-23", time: "18:30", location: "Beierholm, Roskildevej 48, 3400 Hillerød",
    description: "", agenda: "", deadline: "",
    responsible1: null, responsible2: null, boedemester: null, treMinutter: null,
    attendees: [10,8,3,9,15,11,13,14,1,4,5,2],
    absent:    [16,6,7,12],
    fines: [], documents: [], rsvpLog: [],
  },
  {
    id: 105, title: "Brandsikring", subtitle: "Oktober 2025",
    date: "2025-10-21", time: "18:30", location: "Aretelia, Buddingevej 272, 2860 Søborg",
    description: "", agenda: "", deadline: "",
    responsible1: null, responsible2: null, boedemester: null, treMinutter: null,
    attendees: [3,9,15,1,4,5,2],
    absent:    [10,16,6,8,7,11,12,13,14],
    fines: [], documents: [], rsvpLog: [],
  },
  {
    id: 106, title: "Pension/fremtid", subtitle: "November 2025",
    date: "2025-11-11", time: "18:30", location: "Blovsterød Kro, Kongevej 21, 3450 Allerød",
    description: "", agenda: "", deadline: "",
    responsible1: null, responsible2: null, boedemester: null, treMinutter: null,
    attendees: [16,3,9,15,11,12,13,1],
    absent:    [10,6,8,7,14,4,5,2],
    fines: [], documents: [], rsvpLog: [],
  },
  {
    id: 107, title: "Escape Room", subtitle: "December 2025",
    date: "2025-12-09", time: "18:30", location: "#400 Hillerød",
    description: "", agenda: "", deadline: "",
    responsible1: null, responsible2: null, boedemester: null, treMinutter: null,
    attendees: [16,8,3,9,15,11,12,13,14,1,4,5,2],
    absent:    [10,6,7],
    fines: [], documents: [], rsvpLog: [],
  },
  {
    id: 108, title: "Nytårskur", subtitle: "Januar 2026",
    date: "2026-01-23", time: "17:00", location: "København",
    description: "", agenda: "", deadline: "",
    responsible1: null, responsible2: null, boedemester: null, treMinutter: null,
    attendees: [16,3,9,15,11,12,13,1,4,2],
    absent:    [10,6,8,7,14,5],
    fines: [], documents: [], rsvpLog: [],
  },
  {
    id: 109, title: "Teledyne Reson", subtitle: "Februar 2026",
    date: "2026-02-17", time: "18:30", location: "Fabriksvangen 13, 3550 Slangerup",
    description: "", agenda: "", deadline: "",
    responsible1: null, responsible2: null, boedemester: null, treMinutter: null,
    attendees: [8,3,7,9,15,11,13,14,1,4,5,2],
    absent:    [10,16,6,12],
    fines: [], documents: [], rsvpLog: [],
  },
];

// ═══════════════════════════════════════════════════════════
// ATOMS
// ═══════════════════════════════════════════════════════════
const Avatar = ({ member, size = 44, onClick }) => (
  member?.photoURL
    ? <img src={member.photoURL} alt={member.initials} onClick={onClick}
        style={{ width: size, height: size, borderRadius: "50%", flexShrink: 0, objectFit: "cover",
          border: `2px solid ${C.goldDim}`, cursor: onClick ? "pointer" : "default" }} />
    : <div onClick={onClick} style={{
        width: size, height: size, borderRadius: "50%", flexShrink: 0,
        background: C.goldDim + "55", border: `2px solid ${C.goldDim}`,
        display: "flex", alignItems: "center", justifyContent: "center",
        color: C.gold, fontWeight: 700, fontFamily: "Outfit",
        fontSize: Math.round(size * 0.35), letterSpacing: "0.02em",
        cursor: onClick ? "pointer" : "default",
      }}>{member?.initials}</div>
);

const PctRing = ({ pct, size = 64, stroke = 5 }) => {
  if (pct === null || pct === undefined)
    return <div style={{ width: size, height: size, display: "flex", alignItems: "center", justifyContent: "center", color: C.faint, fontSize: 14 }}>–</div>;
  const r = (size - stroke) / 2, circ = 2 * Math.PI * r, arc = (pct / 100) * circ, col = pctColor(pct);
  return (
    <div style={{ position: "relative", width: size, height: size, flexShrink: 0 }}>
      <svg width={size} height={size} style={{ transform: "rotate(-90deg)", display: "block" }}>
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={C.border} strokeWidth={stroke} />
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={col} strokeWidth={stroke}
          strokeDasharray={`${arc} ${circ}`} strokeLinecap="round" />
      </svg>
      <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <span style={{ fontSize: Math.round(size * 0.21), fontWeight: 700, color: col, fontFamily: "Outfit", lineHeight: 1 }}>{pct}%</span>
      </div>
    </div>
  );
};

const Btn = ({ children, onClick, v = "gold", style, disabled }) => {
  const vs = {
    gold:   { bg: C.gold,  color: "#08090b", border: "none" },
    ghost:  { bg: "transparent", color: C.soft, border: `1px solid ${C.border}` },
    danger: { bg: "transparent", color: C.red,  border: `1px solid ${C.red}50` },
    green:  { bg: "transparent", color: C.green, border: `1px solid ${C.green}50` },
    sub:    { bg: C.card,  color: C.white, border: `1px solid ${C.border}` },
  };
  const vv = vs[v] || vs.gold;
  return (
    <button disabled={disabled} onClick={onClick} style={{
      background: vv.bg, color: vv.color, border: vv.border, borderRadius: C.rs,
      padding: "12px 20px", fontSize: 15, fontWeight: 600, fontFamily: "Outfit",
      cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.4 : 1,
      display: "inline-flex", alignItems: "center", gap: 6,
      letterSpacing: "0.01em", minHeight: 48, ...style,
    }}>{children}</button>
  );
};

const SmBtn = ({ children, onClick, v = "ghost", style, disabled }) => (
  <Btn onClick={onClick} v={v} disabled={disabled} style={{ padding: "8px 14px", fontSize: 13, minHeight: 38, ...style }}>{children}</Btn>
);

const Label = ({ children }) => (
  <div style={{ fontSize: 11, color: C.soft, fontWeight: 600, letterSpacing: "0.12em", textTransform: "uppercase", marginBottom: 6, fontFamily: "Outfit" }}>{children}</div>
);

const Inp = ({ label, ...p }) => (
  <div style={{ marginBottom: 16 }}>
    {label && <Label>{label}</Label>}
    <input {...p} style={{ width: "100%", background: C.bg, border: `1px solid ${C.border}`, borderRadius: C.rs, padding: "13px 16px", color: C.white, fontSize: 15, fontFamily: "Outfit", outline: "none", boxSizing: "border-box", ...p.style }} />
  </div>
);

const Txa = ({ label, ...p }) => (
  <div style={{ marginBottom: 16 }}>
    {label && <Label>{label}</Label>}
    <textarea {...p} style={{ width: "100%", background: C.bg, border: `1px solid ${C.border}`, borderRadius: C.rs, padding: "13px 16px", color: C.white, fontSize: 15, fontFamily: "Outfit", outline: "none", boxSizing: "border-box", resize: "vertical", minHeight: 90, ...p.style }} />
  </div>
);

const Sel = ({ label, value, onChange, children }) => (
  <div style={{ marginBottom: 16 }}>
    {label && <Label>{label}</Label>}
    <select value={value || ""} onChange={onChange} style={{ width: "100%", background: C.bg, border: `1px solid ${C.border}`, borderRadius: C.rs, padding: "13px 16px", color: C.white, fontSize: 15, fontFamily: "Outfit", outline: "none" }}>
      {children}
    </select>
  </div>
);

// ═══════════════════════════════════════════════════════════
// MODAL
// ═══════════════════════════════════════════════════════════
const Modal = ({ title, onClose, children, wide, tall }) => (
  <div onClick={e => e.target === e.currentTarget && onClose()} style={{
    position: "fixed", inset: 0, background: "rgba(0,0,0,0.85)", backdropFilter: "blur(6px)",
    zIndex: 600, display: "flex", alignItems: "flex-end", justifyContent: "center",
  }}>
    <div style={{
      background: C.surface, border: `1px solid ${C.border}`,
      borderRadius: "20px 20px 0 0",
      width: "100%", maxHeight: "92vh",
      overflow: "auto", boxShadow: "0 -8px 40px rgba(0,0,0,0.5)",
    }}>
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "18px 22px", borderBottom: `1px solid ${C.border}`,
        position: "sticky", top: 0, background: C.surface, zIndex: 2,
      }}>
        <span style={{ fontFamily: "Outfit", fontSize: 20, fontWeight: 700, color: C.white }}>{title}</span>
        <button onClick={onClose} style={{ background: "none", border: "none", color: C.soft, cursor: "pointer", fontSize: 22, padding: "4px 8px", lineHeight: 1 }}>✕</button>
      </div>
      <div style={{ padding: "22px 22px 28px" }}>{children}</div>
    </div>
  </div>
);

// ═══════════════════════════════════════════════════════════
// LOGIN
// ═══════════════════════════════════════════════════════════
const Login = ({ members, onLogin, onSetPassword, onFetchFresh }) => {
  const [screen, setScreen] = useState("login"); // login | mustChange | forgot
  const [email, setEmail]   = useState("");
  const [pw, setPw]         = useState("");
  const [newPw, setNewPw]   = useState("");
  const [newPw2, setNewPw2] = useState("");
  const [err, setErr]       = useState("");
  const [pendingMember, setPendingMember] = useState(null);
  const [resetDone, setResetDone] = useState(false);

  const go = async () => {
    setErr("");
    const m = members.find(m => m.email && m.email.trim().toLowerCase() === email.trim().toLowerCase());
    const mByName = !m && members.find(mm => mm.name.toLowerCase() === email.trim().toLowerCase());
    const found = m || mByName;
    if (!found) return setErr("Email / navn ikke fundet");

    // Use onFetchFresh to get latest data from Firestore
    const fresh = onFetchFresh ? await onFetchFresh(found.id) || found : found;

    if (fresh.password !== pw) return setErr("Forkert adgangskode");
    if (fresh.mustChangePw) { setPendingMember(fresh); setScreen("mustChange"); return; }
    onLogin(fresh);
  };

  const doChangePassword = () => {
    setErr("");
    if (newPw.length < 6) return setErr("Adgangskode skal være mindst 6 tegn");
    if (newPw !== newPw2) return setErr("Adgangskoderne matcher ikke");
    const updated = { ...pendingMember, password: newPw, mustChangePw: false };
    onSetPassword(updated);
    onLogin(updated);
  };

  const doForgot = async () => {
    setErr("");
    const found = members.find(m => m.email && m.email.trim().toLowerCase() === email.trim().toLowerCase());
    if (!found) return setErr("Email ikke fundet");
    try {
      await setDoc(doc(db, "members", String(found.id)), {
        password: "rt30x6!",
        mustChangePw: true,
      }, { merge: true });
      setResetDone(true);
    } catch(e) {
      setErr("Fejl: " + e.message);
    }
  };

  const Logo = () => (
    <div style={{ textAlign: "center", marginBottom: 40 }}>
      <div style={{ fontFamily: "Outfit", fontSize: 44, fontWeight: 900, color: C.white, letterSpacing: "-0.02em", lineHeight: 1 }}>
        Round <span style={{ color: C.gold }}>Table</span>
      </div>
      <div style={{ fontSize: 13, color: C.gold, fontWeight: 700, letterSpacing: "0.28em", marginTop: 6 }}>30X6</div>
      <div style={{ fontSize: 11, color: C.white, marginTop: 8, letterSpacing: "0.1em" }}>ver. 2.49</div>
    </div>
  );

  const ErrBox = ({ msg }) => msg ? (
    <div style={{ border: `1px solid ${C.red}50`, borderRadius: C.rs, padding: "12px 16px", marginBottom: 18, color: C.red, fontSize: 14 }}>{msg}</div>
  ) : null;

  return (
    <div style={{ minHeight: "100vh", background: C.bg, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", fontFamily: "Outfit", padding: 24 }}>
      <div style={{ width: "100%", maxWidth: 420, padding: "0 16px", boxSizing: "border-box" }}>
        <Logo />

        {/* ─── NORMAL LOGIN ─── */}
        {screen === "login" && (
          <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: C.r, padding: 28 }}>
            <ErrBox msg={err} />
            <Inp label="Email eller navn" type="text" value={email} onChange={e => setEmail(e.target.value)} placeholder="din@email.dk" />
            <Inp label="Adgangskode" type="password" value={pw} onChange={e => setPw(e.target.value)} placeholder="••••••••" onKeyDown={e => e.key === "Enter" && go()} />
            <Btn onClick={go} style={{ width: "100%", justifyContent: "center", marginTop: 4 }}>Log ind</Btn>
            <button onClick={() => { setScreen("forgot"); setErr(""); setResetDone(false); }} style={{ background: "none", border: "none", color: C.soft, cursor: "pointer", fontSize: 13, fontFamily: "Outfit", marginTop: 16, display: "block", textAlign: "center", width: "100%", textDecoration: "underline" }}>
              Glemt adgangskode?
            </button>
          </div>
        )}

        {/* ─── FIRST LOGIN / MUST CHANGE PASSWORD ─── */}
        {screen === "mustChange" && (
          <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: C.r, padding: 28 }}>
            <div style={{ background: C.gold+"18", border: `1px solid ${C.gold}40`, borderRadius: C.rs, padding: "12px 16px", marginBottom: 20, fontSize: 14, color: C.gold }}>
              Velkommen, {pendingMember?.name.split(" ")[0]}! Du logger ind for første gang. Vælg venligst din egen adgangskode.
            </div>
            <ErrBox msg={err} />
            <Inp label="Ny adgangskode" type="password" value={newPw} onChange={e => setNewPw(e.target.value)} placeholder="Mindst 6 tegn" />
            <Inp label="Gentag adgangskode" type="password" value={newPw2} onChange={e => setNewPw2(e.target.value)} placeholder="••••••••" onKeyDown={e => e.key === "Enter" && doChangePassword()} />
            <Btn onClick={doChangePassword} style={{ width: "100%", justifyContent: "center" }}>Gem og log ind</Btn>
          </div>
        )}

        {/* ─── FORGOT PASSWORD ─── */}
        {screen === "forgot" && (
          <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: C.r, padding: 28 }}>
            {resetDone ? (
              <div>
                <div style={{ background: C.green+"18", border: `1px solid ${C.green}40`, borderRadius: C.rs, padding: "14px 16px", color: C.green, fontSize: 14, marginBottom: 20 }}>
                  Din adgangskode er nulstillet. Log ind og skift den straks.
                </div>
                <Btn onClick={() => { setScreen("login"); setErr(""); }} style={{ width: "100%", justifyContent: "center" }}>← Tilbage til login</Btn>
              </div>
            ) : (
              <div>
                <div style={{ fontSize: 15, color: C.white, fontWeight: 600, marginBottom: 6 }}>Glemt adgangskode</div>
                <div style={{ fontSize: 13, color: C.soft, marginBottom: 20 }}>Indtast din email, og din adgangskode nulstilles.</div>
                <ErrBox msg={err} />
                <Inp label="Email" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="din@email.dk" />
                <Btn onClick={doForgot} style={{ width: "100%", justifyContent: "center" }}>Nulstil adgangskode</Btn>
                <button onClick={() => { setScreen("login"); setErr(""); }} style={{ background: "none", border: "none", color: C.soft, cursor: "pointer", fontSize: 13, fontFamily: "Outfit", marginTop: 14, display: "block", textAlign: "center", width: "100%", textDecoration: "underline" }}>
                  ← Tilbage til login
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════
// UPCOMING MEETING CARD
// ═══════════════════════════════════════════════════════════
const UpcomingCard = ({ meeting, members, user, isAdmin, onView, onRSVP, onFines, onRsvpLog }) => {
  const days   = daysUntil(meeting.date);
  const isAtt  = meeting.attendees.includes(user.id);
  const isAbs  = meeting.absent.includes(user.id);
  const canFines = (meeting.boedemester === user.id || isAdmin) && meeting.boedemester;
  const totalM = members.length;
  const pct    = meetingAttPct(meeting, totalM);

  return (
    <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: C.r, overflow: "hidden" }}>
      <div style={{ height: 4, background: `linear-gradient(90deg, ${C.gold}, transparent 70%)` }} />
      <div style={{ padding: "22px 20px 18px" }}>
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, marginBottom: 16 }}>
          <div>
            {meeting.isBoardMeeting && (
              <div style={{ fontSize: 11, color: C.gold, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 4 }}>
                🏛️ Bestyrelsesmøde
              </div>
            )}
            {meeting.noAttendance && (
              <div style={{ fontSize: 11, color: C.yellow, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 4 }}>
                ★ Ikke mødepligt
              </div>
            )}
            <div style={{ fontSize: 12, color: C.gold, fontWeight: 600, letterSpacing: "0.14em", textTransform: "uppercase", marginBottom: 4 }}>{meeting.subtitle}</div>
            <h3 style={{ margin: 0, fontFamily: "Outfit", fontSize: 28, fontWeight: 900, color: C.white, lineHeight: 1.1 }}>{meeting.title}</h3>
          </div>
          <div style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: C.rs, padding: "10px 14px", textAlign: "center", flexShrink: 0 }}>
            <div style={{ fontFamily: "Outfit", fontSize: 30, fontWeight: 900, color: C.gold, lineHeight: 1 }}>{days}</div>
            <div style={{ fontSize: 10, color: C.soft, letterSpacing: "0.1em" }}>DAGE</div>
          </div>
        </div>
        {meeting.description && <DescriptionBlock text={meeting.description} style={{ margin: "0 0 16px" }} />}
        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 18 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 15, color: C.white }}>
            <span style={{ color: C.gold }}>📅</span>
            <span style={{ flex: 1 }}>{fmtLong(meeting.date)} · kl. {meeting.time}</span>
            <button onClick={() => downloadICS(meeting)} style={{ background: C.gold+"20", border:`1px solid ${C.gold}50`, borderRadius: C.rs, padding: "5px 12px", color: C.gold, fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "Outfit", whiteSpace: "nowrap" }}>＋ Kalender</button>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 15, color: C.white }}>
            <span style={{ color: C.gold }}>📍</span>
            <MapLink location={meeting.location} />
          </div>
          {meeting.deadline && (() => {
            const dl = new Date(meeting.deadline);
            const daysLeft = Math.ceil((dl - TODAY) / 86400000);
            const isPast = daysLeft < 0;
            const isClose = daysLeft >= 0 && daysLeft <= 2;
            const col = isPast ? C.red : isClose ? C.yellow : C.soft;
            return (
              <div style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 14 }}>
                <span style={{ color: col }}>⏰</span>
                <span style={{ color: col, fontWeight: isPast || isClose ? 600 : 400 }}>
                  {isPast
                    ? `Tilmeldingsfrist udløbet (${fmtShort(meeting.deadline)})`
                    : daysLeft === 0
                    ? `Tilmeldingsfrist: I DAG`
                    : `Tilmeldingsfrist: ${fmtShort(meeting.deadline)} (${daysLeft} dage)`}
                </span>
              </div>
            );
          })()}
        </div>
        {(meeting.responsible1 || meeting.boedemester || meeting.treMinutter) && (
          <div style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: C.rs, padding: "14px 16px", marginBottom: 18 }}>
            {meeting.responsible1 && (
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 14, marginBottom: 8 }}>
                <span style={{ color: C.soft }}>Ansvarlig</span>
                <span style={{ color: C.white, fontWeight: 600 }}>
                  {getMember(members, meeting.responsible1)?.name}{meeting.responsible2 ? " & " + getMember(members, meeting.responsible2)?.name : ""}
                </span>
              </div>
            )}
            {meeting.boedemester && (
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 14, marginBottom: meeting.treMinutter ? 8 : 0 }}>
                <span style={{ color: C.soft }}>Bødemester</span>
                <span style={{ color: C.white, fontWeight: 600 }}>{getMember(members, meeting.boedemester)?.name}</span>
              </div>
            )}
            {meeting.treMinutter && (
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 14 }}>
                <span style={{ color: C.soft }}>3 Minutter</span>
                <span style={{ color: C.white, fontWeight: 600 }}>{getMember(members, meeting.treMinutter)?.name}</span>
              </div>
            )}
          </div>
        )}
        {/* Attendance */}
        <div style={{ display: "flex", alignItems: "center", gap: 18 }}>
          <PctRing pct={pct} size={70} stroke={6} />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 14, color: C.soft, marginBottom: 8 }}>Mødeprocent</div>
            <div style={{ display: "flex", gap: 16 }}>
              <div>
                <div style={{ fontSize: 24, fontWeight: 900, fontFamily: "Outfit", color: C.green }}>{meeting.attendees.length}</div>
                <div style={{ fontSize: 11, color: C.green, letterSpacing: "0.08em" }}>TILMELDT</div>
              </div>
              <div>
                <div style={{ fontSize: 24, fontWeight: 900, fontFamily: "Outfit", color: C.red }}>{meeting.absent.length}</div>
                <div style={{ fontSize: 11, color: C.red, letterSpacing: "0.08em" }}>AFBUD</div>
              </div>
              <div>
                <div style={{ fontSize: 24, fontWeight: 900, fontFamily: "Outfit", color: C.soft }}>{totalM - meeting.attendees.length - meeting.absent.length}</div>
                <div style={{ fontSize: 11, color: C.soft, letterSpacing: "0.08em" }}>UBESVARET</div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, borderTop: `1px solid ${C.border}`, padding: "14px 20px", background: C.bg + "cc" }}>
        <SmBtn v="sub" onClick={onView}>Detaljer</SmBtn>
        <SmBtn v={isAtt ? "green" : "ghost"} onClick={() => onRSVP(true)}>✓ Tilmeld</SmBtn>
        <SmBtn v={isAbs ? "danger" : "ghost"} onClick={() => onRSVP(false)}>✕ Afmeld</SmBtn>
        {canFines && <SmBtn v="ghost" onClick={onFines} style={{ marginLeft: "auto", color: C.gold, borderColor: C.gold + "50" }}>⚖️ Bødeliste</SmBtn>}
        {(isAdmin || meeting.responsible1 === user?.id || meeting.responsible2 === user?.id) && <SmBtn v="ghost" onClick={onView} style={{ color: C.gold, borderColor: C.gold + "40" }}>✎ Redigér</SmBtn>}
        {onRsvpLog && <SmBtn v="ghost" onClick={onRsvpLog} style={{ color: C.soft, fontSize: 12 }}>📋 Log</SmBtn>}
      </div>
    </div>
  );
};

// ─── FINES OVERVIEW ───────────────────────────────────────────────────────────
const FineRow = ({ member, total, details }) => {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: C.r, overflow: "hidden" }}>
      <div onClick={() => setOpen(o => !o)} style={{ display: "flex", alignItems: "center", gap: 14, padding: "16px 20px", cursor: "pointer" }}>
        <Avatar member={member} size={40} />
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: C.white }}>{member.name}</div>
          <div style={{ fontSize: 13, color: C.soft }}>{details.length} bøde{details.length !== 1 ? "r" : ""}</div>
        </div>
        <div style={{ fontSize: 20, fontWeight: 900, color: C.gold }}>{total} kr</div>
        <div style={{ color: C.soft, fontSize: 14 }}>{open ? "▲" : "▼"}</div>
      </div>
      {open && (
        <div style={{ borderTop: `1px solid ${C.border}`, padding: "12px 20px", display: "flex", flexDirection: "column", gap: 8 }}>
          {details.map((f, i) => (
            <div key={i} style={{ display: "flex", justifyContent: "space-between", fontSize: 14 }}>
              <span style={{ color: C.soft }}>{f.meetingTitle} · {fmtShort(f.meetingDate)} — {f.note}</span>
              <span style={{ color: C.gold, fontWeight: 600, flexShrink: 0, marginLeft: 12 }}>{f.amount} kr</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const FinesOverview = ({ members, meetings }) => {
  const cyMeetings = meetings.filter(m => isPast(m.date) && new Date(m.date) >= CY_START);
  const memberFines = members.map(member => {
    const total = cyMeetings.reduce((sum, mtg) => {
      return sum + (mtg.fines || []).filter(f => f.memberId === member.id && f.amount).reduce((s, f) => s + (parseFloat(f.amount) || 0), 0);
    }, 0);
    const details = cyMeetings.flatMap(mtg =>
      (mtg.fines || []).filter(f => f.memberId === member.id && f.amount).map(f => ({ ...f, meetingTitle: mtg.title, meetingDate: mtg.date }))
    );
    return { member, total, details };
  }).filter(x => x.total > 0).sort((a, b) => b.total - a.total);

  const grandTotal = memberFines.reduce((s, x) => s + x.total, 0);

  return (
    <div>
      <div style={{ marginBottom: 28 }}>
        <h1 style={{ margin: "0 0 4px", fontFamily: "Outfit", fontSize: 36, fontWeight: 900, color: C.white }}>Bøder</h1>
        <div style={{ fontSize: 14, color: C.soft }}>
          Klubår: 1. maj {CY_START.getFullYear()} – 30. april {CY_START.getFullYear()+1} · Total: <span style={{ color: C.gold, fontWeight: 700 }}>{grandTotal} kr</span>
        </div>
      </div>
      {memberFines.length === 0 ? (
        <div style={{ color: C.faint, fontSize: 16, textAlign: "center", marginTop: 60 }}>Ingen bøder dette klubår 🎉</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {memberFines.map(({ member, total, details }) => (
            <FineRow key={member.id} member={member} total={total} details={details} />
          ))}
        </div>
      )}
    </div>
  );
};

// ─── ARCHIVE ROW ──────────────────────────────────────────────────────────────
const ArchiveRow = ({ meeting, members, user, isAdmin, onClick, onFines }) => {
  const totalM = members.length;
  const pct    = meetingAttPct(meeting, totalM);
  const d      = new Date(meeting.date);
  const canFines = isAdmin || meeting.boedemester === user?.id;
  return (
    <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: C.rs, padding: "16px 20px", transition: "border-color 0.15s", display: "flex", alignItems: "center", gap: 16 }}
      onMouseEnter={e => e.currentTarget.style.borderColor = C.borderHi}
      onMouseLeave={e => e.currentTarget.style.borderColor = C.border}
    >
      <div onClick={onClick} style={{ cursor: "pointer", display: "flex", alignItems: "center", gap: 16, flex: 1 }}>
        <div style={{ textAlign: "center", minWidth: 46 }}>
          <div style={{ fontFamily: "Outfit", fontSize: 26, fontWeight: 900, color: C.soft, lineHeight: 1 }}>{d.getDate()}</div>
          <div style={{ fontSize: 10, color: C.faint, textTransform: "uppercase", letterSpacing: "0.08em" }}>{d.toLocaleDateString("da-DK", { month: "short" })}</div>
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 16, fontWeight: 600, color: C.white, marginBottom: 4 }}>{meeting.title} – {meeting.subtitle}</div>
          <div style={{ fontSize: 13, color: C.soft, display: "flex", gap: 12, flexWrap: "wrap" }}>
            <span style={{ color: C.green }}>{meeting.attendees.length} tilmeldt</span>
            <span style={{ color: C.red }}>{meeting.absent.length} afbud</span>
          </div>
        </div>
        <PctRing pct={pct} size={56} stroke={5} />
      </div>
      {canFines && onFines && (
        <button onClick={e => { e.stopPropagation(); onFines(); }} style={{
          background: C.gold+"22", border: `1px solid ${C.gold}50`, borderRadius: C.rs,
          padding: "6px 12px", color: C.gold, fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "Outfit", flexShrink: 0,
        }}>⚖️ Bøder</button>
      )}
    </div>
  );
};

// ─── MEMBER CARD ──────────────────────────────────────────────────────────────
const MemberCard = ({ member, meetings, isAdmin, onEdit, onDelete, onResetPw }) => {
  const pct = memberAttPct(member.id, meetings);
  const [confirmDelete, setConfirmDelete] = useState(false);
  return (
    <div style={{ background: C.surface, border: `1px solid ${confirmDelete ? C.red+"80" : C.border}`, borderRadius: C.r, padding: 20, position: "relative", overflow: "hidden", transition: "border-color 0.2s" }}>
      <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: `linear-gradient(90deg, ${C.gold}70, transparent)` }} />
      <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 14 }}>
        <Avatar member={member} size={52} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontFamily: "Outfit", fontWeight: 700, color: C.white, fontSize: 18, lineHeight: 1.2, marginBottom: 4 }}>{member.name}</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            <span style={{ background: C.gold + "20", color: C.gold, border: `1px solid ${C.gold}40`, padding: "2px 10px", borderRadius: 20, fontSize: 11, fontWeight: 600, letterSpacing: "0.06em" }}>
              {member.clubRole}
            </span>
            {member.sysRole === "admin" && (
              <span style={{ background: C.white + "12", color: C.white, border: `1px solid ${C.white}20`, padding: "2px 10px", borderRadius: 20, fontSize: 11, fontWeight: 600, letterSpacing: "0.06em" }}>
                Administrator
              </span>
            )}
            {member.board && (
              <span style={{ background: C.faint, color: C.soft, border: `1px solid ${C.border}`, padding: "2px 10px", borderRadius: 20, fontSize: 11, fontWeight: 600 }}>
                Bestyrelsen
              </span>
            )}
          </div>
        </div>
        <PctRing pct={pct} size={64} stroke={5} />
      </div>
      {(member.email || member.phone || member.address) && (
        <div style={{ display: "flex", flexDirection: "column", gap: 7, marginBottom: 14 }}>
          {member.email   && <a href={`mailto:${member.email}`} style={{ display: "flex", gap: 10, fontSize: 14, color: C.soft, textDecoration: "none" }}><span>✉️</span><span style={{ borderBottom: `1px solid ${C.faint}` }}>{member.email}</span></a>}
          {member.phone   && (
            <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
              <a href={`tel:${member.phone.replace(/\s/g,"")}`} style={{ display: "flex", gap: 10, fontSize: 14, color: C.soft, textDecoration: "none", flex: 1 }}>
                <span>📞</span><span style={{ borderBottom: `1px solid ${C.faint}` }}>{member.phone}</span>
              </a>
              <a href={`sms:${member.phone.replace(/\s/g,"")}`} style={{ fontSize: 12, color: C.gold, textDecoration: "none", background: C.gold+"18", border: `1px solid ${C.gold}40`, borderRadius: C.rs, padding: "2px 10px", fontWeight: 600, fontFamily: "Outfit" }}>SMS</a>
            </div>
          )}
          {member.address && <div style={{ display: "flex", gap: 10, fontSize: 14, color: C.soft }}><span>📍</span><span>{member.address}</span></div>}
        </div>
      )}
      {/* Admin actions */}
      {isAdmin && !confirmDelete && (
        <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
          <SmBtn v="ghost" onClick={onEdit} style={{ flex: 1, justifyContent: "center" }}>✎ Redigér</SmBtn>
          <SmBtn v="ghost" onClick={onResetPw} style={{ color: C.gold, borderColor: C.gold+"40" }}>🔑</SmBtn>
          <SmBtn v="danger" onClick={() => setConfirmDelete(true)}>✕ Slet</SmBtn>
        </div>
      )}

      {/* Inline delete confirmation */}
      {isAdmin && confirmDelete && (
        <div style={{ background: C.red + "12", border: `1px solid ${C.red}40`, borderRadius: C.rs, padding: "14px 16px" }}>
          <div style={{ fontSize: 14, color: C.white, marginBottom: 12 }}>
            Slet <strong>{member.name}</strong>? Dette kan ikke fortrydes.
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <SmBtn v="ghost" onClick={() => setConfirmDelete(false)} style={{ flex: 1, justifyContent: "center" }}>Annullér</SmBtn>
            <SmBtn v="danger" onClick={onDelete} style={{ flex: 1, justifyContent: "center" }}>✕ Ja, slet</SmBtn>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── FINES MODAL ──────────────────────────────────────────────────────────────
const FinesModal = ({ meeting, members, isAdmin, user, onClose, onSave }) => {
  const uidRef = useRef((meeting.fines || []).length);
  const [fines, setFines] = useState(
    (meeting.fines || []).map((f, i) => ({ ...f, _uid: i }))
  );
  const canEdit = meeting.boedemester === user.id || isAdmin;

  const addFineRow  = (memberId) =>
    setFines(p => [...p, { _uid: uidRef.current++, memberId, amount: "", note: "" }]);
  const updFineRow  = (uid, field, val) =>
    setFines(p => p.map(f => f._uid === uid ? { ...f, [field]: val } : f));
  const delFineRow  = (uid) =>
    setFines(p => p.filter(f => f._uid !== uid));

  const total       = fines.reduce((s, f) => s + (parseFloat(f.amount) || 0), 0);
  const mFines      = (mid) => fines.filter(f => f.memberId === mid);
  const mTotal      = (mid) => mFines(mid).reduce((s, f) => s + (parseFloat(f.amount) || 0), 0);

  const unansweredIds = members
    .filter(m => !meeting.attendees.includes(m.id) && !meeting.absent.includes(m.id))
    .map(m => m.id);
  const groups = [
    { g: "att", label: "Tilmeldte",   ids: meeting.attendees, col: C.green },
    { g: "abs", label: "Afbud",       ids: meeting.absent,    col: C.red },
    { g: "un",  label: "Ikke svaret", ids: unansweredIds,     col: C.soft },
  ];

  const inpStyle = (colr) => ({
    background: C.surface, border: `1px solid ${C.border}`, borderRadius: C.rs,
    padding: "10px 12px", color: colr, fontSize: 14, fontFamily: "Outfit", outline: "none", width: "100%", boxSizing: "border-box",
  });

  return (
    <Modal title={`Bødeliste – ${meeting.title}`} onClose={onClose} wide tall>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <span style={{ fontSize: 14, color: C.soft }}>
          Bødemester: <span style={{ color: C.white, fontWeight: 600 }}>{getMember(members, meeting.boedemester)?.name || "–"}</span>
        </span>
        {total > 0 && <span style={{ fontSize: 16, fontWeight: 700, color: C.gold }}>{total} kr total</span>}
      </div>

      {groups.map(({ g, label, ids, col }) => {
        if (!ids.length) return null;
        return (
          <div key={g} style={{ marginBottom: 24 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: col, letterSpacing: "0.12em", textTransform: "uppercase", marginBottom: 10 }}>
              {label} ({ids.length})
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {ids.map(id => {
                const m = getMember(members, id);
                if (!m) return null;
                const rows  = mFines(m.id);
                const mtot  = mTotal(m.id);
                return (
                  <div key={m.id} style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: C.rs, padding: 14 }}>
                    {/* Member header row */}
                    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: rows.length ? 10 : 0 }}>
                      <Avatar member={m} size={38} />
                      <span style={{ fontWeight: 600, color: g === "att" ? C.white : C.soft, fontSize: 15, flex: 1 }}>{m.name}</span>
                      {mtot > 0 && <span style={{ color: C.gold, fontWeight: 700, fontSize: 14 }}>{mtot} kr</span>}
                      {canEdit && (
                        <button onClick={() => addFineRow(m.id)} title="Tilføj bøde" style={{
                          background: C.gold + "22", border: `1px solid ${C.gold}50`, borderRadius: C.rs,
                          width: 34, height: 34, cursor: "pointer", color: C.gold, fontSize: 22,
                          display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                        }}>＋</button>
                      )}
                    </div>

                    {/* Fine rows */}
                    {rows.map((fine, fi) => (
                      <div key={fine._uid} style={{ display: "grid", gridTemplateColumns: "100px 1fr 34px", gap: 8, marginBottom: fi < rows.length - 1 ? 8 : 0 }}>
                        <input type="number" placeholder="Beløb kr" value={fine.amount}
                          onChange={e => updFineRow(fine._uid, "amount", e.target.value)}
                          readOnly={!canEdit} style={inpStyle(C.gold)} />
                        <input placeholder="Begrundelse…" value={fine.note}
                          onChange={e => updFineRow(fine._uid, "note", e.target.value)}
                          readOnly={!canEdit} style={inpStyle(C.white)} />
                        {canEdit && (
                          <button onClick={() => delFineRow(fine._uid)} style={{
                            background: "transparent", border: `1px solid ${C.red}40`,
                            borderRadius: C.rs, cursor: "pointer", color: C.red, fontSize: 16,
                            display: "flex", alignItems: "center", justifyContent: "center",
                          }}>✕</button>
                        )}
                      </div>
                    ))}

                    {rows.length === 0 && !canEdit && (
                      <div style={{ fontSize: 12, color: C.faint }}>Ingen bøder</div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}

      {canEdit && (
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, borderTop: `1px solid ${C.border}`, paddingTop: 18 }}>
          <SmBtn v="ghost" onClick={onClose}>Annullér</SmBtn>
          <Btn onClick={() => onSave(fines.filter(f => f.amount || f.note).map(({ _uid, ...f }) => f))}>
            Gem bødeliste
          </Btn>
        </div>
      )}
    </Modal>
  );
};

// ─── DOCUMENT SECTION ─────────────────────────────────────────────────────────
const DocSection = ({ docs, onAdd, canUpload }) => {
  const ref = useRef();
  const iconFor = n => n.endsWith(".pdf") ? "📄" : n.endsWith(".xlsx") || n.endsWith(".xls") ? "📊" : "📎";
  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
        <Label>Dokumenter ({docs.length})</Label>
        {canUpload && <>
          <input ref={ref} type="file" multiple style={{ display: "none" }} onChange={e => { Array.from(e.target.files).forEach(f => onAdd({ name: f.name, size: (f.size/1024).toFixed(0)+" KB", uploadedAt: TODAY.toISOString().split("T")[0] })); e.target.value = ""; }} />
          <SmBtn v="ghost" onClick={() => ref.current.click()}>⬆ Upload</SmBtn>
        </>}
      </div>
      {docs.length === 0
        ? <div style={{ fontSize: 14, color: C.faint }}>Ingen dokumenter tilknyttet.</div>
        : docs.map((doc, i) => (
          <div key={i} style={{ display: "flex", alignItems: "center", gap: 12, background: C.bg, border: `1px solid ${C.border}`, borderRadius: C.rs, padding: "12px 16px", marginBottom: 8 }}>
            <span style={{ fontSize: 20 }}>{iconFor(doc.name)}</span>
            <div style={{ flex: 1 }}><div style={{ fontSize: 14, color: C.white }}>{doc.name}</div><div style={{ fontSize: 12, color: C.faint }}>{doc.size} · {doc.uploadedAt}</div></div>
            <button style={{ background: "none", border: "none", color: C.soft, cursor: "pointer", fontSize: 14, fontFamily: "Outfit" }}>↓</button>
          </div>
        ))
      }
    </div>
  );
};

// ─── MAP LINK ─────────────────────────────────────────────────────────────────
const MapLink = ({ location, style }) => {
  const [open, setOpen] = useState(false);
  if (!location) return <span style={style}>–</span>;
  const enc = encodeURIComponent(location);
  return (
    <div style={{ position: "relative", display: "inline-block" }}>
      <span onClick={() => setOpen(o => !o)} style={{
        color: C.white, textDecoration: "underline", textDecorationColor: C.faint,
        cursor: "pointer", ...style,
      }}>{location}</span>
      {open && (
        <div style={{
          position: "absolute", top: "100%", left: 0, zIndex: 300, marginTop: 6,
          background: C.surface, border: `1px solid ${C.border}`, borderRadius: C.rs,
          padding: 8, display: "flex", flexDirection: "column", gap: 6, minWidth: 180,
          boxShadow: "0 8px 24px #0006",
        }}>
          <div style={{ fontSize: 11, color: C.soft, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 2, paddingLeft: 4 }}>Åbn i kort</div>
          <a href={`https://maps.apple.com/?q=${enc}`} target="_blank" rel="noopener noreferrer"
            onClick={() => setOpen(false)}
            style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 10px", borderRadius: C.rs, background: C.bg, color: C.white, textDecoration: "none", fontSize: 14, fontWeight: 600 }}>
            🍎 Apple Maps
          </a>
          <a href={`https://www.google.com/maps/search/?api=1&query=${enc}`} target="_blank" rel="noopener noreferrer"
            onClick={() => setOpen(false)}
            style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 10px", borderRadius: C.rs, background: C.bg, color: C.white, textDecoration: "none", fontSize: 14, fontWeight: 600 }}>
            🗺️ Google Maps
          </a>
          <button onClick={() => setOpen(false)} style={{ background: "none", border: "none", color: C.faint, fontSize: 12, cursor: "pointer", fontFamily: "Outfit", textAlign: "left", padding: "4px 10px" }}>Annullér</button>
        </div>
      )}
    </div>
  );
};

// ─── DESCRIPTION BLOCK (max 4 lines with expand) ─────────────────────────────
const DescriptionBlock = ({ text, style }) => {
  const [expanded, setExpanded] = useState(false);
  if (!text) return null;
  const lines = text.split("\n");
  const MAX_LINES = 4;
  const isLong = lines.length > MAX_LINES || text.length > 200;
  return (
    <div style={{ marginBottom: 16, ...style }}>
      <p style={{
        margin: 0, fontSize: 15, color: C.soft, lineHeight: 1.6,
        display: "-webkit-box", WebkitLineClamp: expanded ? "unset" : MAX_LINES,
        WebkitBoxOrient: "vertical", overflow: expanded ? "visible" : "hidden",
      }}>{text}</p>
      {isLong && (
        <button onClick={() => setExpanded(e => !e)} style={{ background: "none", border: "none", color: C.gold, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "Outfit", padding: 0, marginTop: 4 }}>
          {expanded ? "▲ Mindre" : "▼ Mere..."}
        </button>
      )}
    </div>
  );
};

// ─── AGENDA BLOCK ─────────────────────────────────────────────────────────────
const AgendaBlock = ({ agenda }) => {
  const lines = agenda.split("\n");
  const MAX = 4;
  const [expanded, setExpanded] = useState(false);
  const shown = expanded ? lines : lines.slice(0, MAX);
  return (
    <div style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: C.rs, padding: 16, marginBottom: 16 }}>
      <Label>Dagsorden</Label>
      <pre style={{ color: C.soft, fontSize: 14, lineHeight: 1.7, margin: 0, whiteSpace: "pre-wrap", fontFamily: "Outfit" }}>{shown.join("\n")}</pre>
      {lines.length > MAX && (
        <button onClick={() => setExpanded(e => !e)} style={{ background: "none", border: "none", color: C.gold, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "Outfit", marginTop: 8, padding: 0 }}>
          {expanded ? "▲ Mindre" : `▼ Mere (${lines.length - MAX} linjer mere)`}
        </button>
      )}
    </div>
  );
};

// ─── MEETING MODAL (view / edit / new) ────────────────────────────────────────
const MeetingModal = ({ mode, meeting, members, isAdmin, user, onClose, onSave, onDelete, onDocAdd, onAdminRsvp, prefill }) => {
  const blank = { title:"", subtitle:"", date:"", time:"18:30", location:"", description:"", agenda:"", responsible1:null, responsible2:null, boedemester:null, treMinutter:null, attendees:[], absent:[], fines:[], documents:[], noAttendance:false, isBoardMeeting:false };
  const [currentMode, setCurrentMode] = useState(mode);
  const [form, setForm] = useState(mode === "new" ? { ...blank, ...prefill } : { ...meeting });
  const s = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const totalM = members.length;
  const [confirmDelete, setConfirmDelete] = useState(false);

  if (currentMode === "view") {
    const pct = meetingAttPct(meeting, totalM);
    return (
      <Modal title={meeting.title} onClose={onClose} wide tall>
        <div style={{ display: "flex", alignItems: "center", gap: 20, marginBottom: 24, background: C.bg, border: `1px solid ${C.border}`, borderRadius: C.rs, padding: 20 }}>
          <PctRing pct={pct} size={80} stroke={7} />
          <div style={{ flex: 1, display: "flex", gap: 24 }}>
            {[["Tilmeldt", meeting.attendees.length, C.green],["Afbud", meeting.absent.length, C.red],["Ubesvaret", totalM-meeting.attendees.length-meeting.absent.length, C.soft]].map(([l,v,c]) => (
              <div key={l}>
                <div style={{ fontSize: 32, fontWeight: 900, fontFamily: "Outfit", color: c }}>{v}</div>
                <div style={{ fontSize: 11, color: c, letterSpacing: "0.1em" }}>{l.toUpperCase()}</div>
              </div>
            ))}
          </div>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 20 }}>
          {[["Dato", fmtFull(meeting.date)+" kl. "+meeting.time, "date"],["Sted", meeting.location, "location"],
            ["Ansvarlig", [meeting.responsible1, meeting.responsible2].filter(Boolean).map(id => getMember(members,id)?.name).join(" & ")||"–", ""],
            ["Bødemester", getMember(members,meeting.boedemester)?.name||"–", ""],
            ["3 Minutter", getMember(members,meeting.treMinutter)?.name||"–", ""]].map(([k,v,type]) => (
            <div key={k} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 15, borderBottom: `1px solid ${C.border}`, paddingBottom: 10, gap: 12 }}>
              <span style={{ color: C.soft, flexShrink: 0 }}>{k}</span>
              <div style={{ display: "flex", alignItems: "center", gap: 10, justifyContent: "flex-end", flex: 1, minWidth: 0 }}>
                {type === "location" && v ? (
                  <MapLink location={v} style={{ fontWeight: 500, textAlign: "right" }} />
                ) : (
                  <span style={{ color: C.white, fontWeight: 500, textAlign: "right" }}>{v}</span>
                )}
                {type === "date" && <button onClick={() => downloadICS(meeting)} style={{ background: C.gold+"20", border:`1px solid ${C.gold}50`, borderRadius: C.rs, padding: "4px 10px", color: C.gold, fontSize: 11, fontWeight: 600, cursor: "pointer", fontFamily: "Outfit", whiteSpace: "nowrap", flexShrink: 0 }}>＋ Kalender</button>}
              </div>
            </div>
          ))}
        </div>
        {meeting.description && <p style={{ fontSize: 15, color: C.soft, lineHeight: 1.65, marginBottom: 16 }}>{meeting.description}</p>}
        {meeting.agenda && <AgendaBlock agenda={meeting.agenda} />}

        {/* Original tilmeldte/afbud grid — synlig for alle */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
          {[["Tilmeldte", meeting.attendees, C.green],["Afbud", meeting.absent, C.red]].map(([lbl,ids,col]) => (
            <div key={lbl} style={{ background: C.bg, border: `1px solid ${col}25`, borderRadius: C.rs, padding: 14 }}>
              <div style={{ fontSize: 11, color: col, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 10 }}>{lbl} ({ids.length})</div>
              {ids.map(id => { const m = getMember(members, id); return m ? <div key={id} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}><Avatar member={m} size={28}/><span style={{ fontSize: 14, color: C.white }}>{m.name}</span></div> : null; })}
              {ids.length === 0 && <div style={{ fontSize: 13, color: C.faint }}>Ingen</div>}
            </div>
          ))}
        </div>

        {/* Admin: styr tilmelding for alle medlemmer */}
        {isAdmin && (
          <div style={{ background: C.bg, border: `1px solid ${C.gold}30`, borderRadius: C.rs, padding: 14, marginBottom: 16 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: C.gold, letterSpacing: "0.12em", textTransform: "uppercase", marginBottom: 12 }}>
              ✎ Admin — styr tilmeldinger
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {members.map(m => {
                const isAtt = meeting.attendees.includes(m.id);
                const isAbs = meeting.absent.includes(m.id);
                const status = isAtt ? "att" : isAbs ? "abs" : "un";
                return (
                  <div key={m.id} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <Avatar member={m} size={28} />
                    <span style={{ flex: 1, fontSize: 13, color: C.white }}>{m.name}</span>
                    <div style={{ display: "flex", gap: 5 }}>
                      {[["att","✓",C.green],["abs","✕",C.red],["un","?",C.faint]].map(([s,icon,col]) => (
                        <button key={s} onClick={() => onAdminRsvp(meeting.id, m.id, s)} style={{
                          width: 30, height: 30, borderRadius: C.rs, cursor: "pointer", fontWeight: 700, fontSize: 13,
                          background: status === s ? col + "33" : "transparent",
                          border: `1px solid ${status === s ? col : C.border}`,
                          color: status === s ? col : C.faint,
                        }}>{icon}</button>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
        {(meeting.fines||[]).filter(f=>f.amount).length > 0 && (
          <div style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: C.rs, padding: 14, marginBottom: 16 }}>
            <div style={{ fontSize: 11, color: C.gold, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 10 }}>Bøder – {(meeting.fines||[]).reduce((s,f)=>s+(parseFloat(f.amount)||0),0)} kr</div>
            {(meeting.fines||[]).filter(f=>f.amount).map((f,i) => (
              <div key={i} style={{ display: "flex", justifyContent: "space-between", fontSize: 14, color: C.soft, marginBottom: 6 }}>
                <span>{getMember(members,f.memberId)?.name}: {f.note}</span>
                <span style={{ color: C.gold, fontWeight: 600 }}>{f.amount} kr</span>
              </div>
            ))}
          </div>
        )}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          {(isAdmin || meeting.responsible1 === user?.id || meeting.responsible2 === user?.id) && <SmBtn v="ghost" onClick={() => setCurrentMode("edit")} style={{ color: C.gold, borderColor: C.gold+"40" }}>✎ Redigér møde</SmBtn>}
          <SmBtn v="ghost" onClick={onClose}>Luk</SmBtn>
        </div>
      </Modal>
    );
  }

  return (
    <Modal title={currentMode === "new" ? "Nyt møde" : "Redigér møde"} onClose={onClose} wide>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <Inp label="Titel" value={form.title} onChange={e => s("title",e.target.value)} placeholder="Møde #47" />
        <Inp label="Undertitel" value={form.subtitle} onChange={e => s("subtitle",e.target.value)} placeholder="Marts 2026" />
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 12, marginBottom: 4 }}>
        <div>
          <Label>📅 Mødedato</Label>
          <input type="date" value={form.date} onChange={e => s("date",e.target.value)}
            style={{ background: C.bg, border: `1px solid ${C.gold}60`, borderRadius: C.rs, padding: "13px 16px", color: C.white, fontSize: 16, fontFamily: "Outfit", outline: "none", cursor: "pointer", display: "block" }} />
        </div>
        <div>
          <Label>🕐 Tidspunkt</Label>
          <input type="time" value={form.time} onChange={e => s("time",e.target.value)}
            style={{ background: C.bg, border: `1px solid ${C.gold}60`, borderRadius: C.rs, padding: "13px 16px", color: C.white, fontSize: 16, fontFamily: "Outfit", outline: "none", cursor: "pointer", display: "block" }} />
        </div>
        <div>
          <Label>⏰ Tilmeldingsfrist (valgfri)</Label>
          <input type="date" value={form.deadline||""} onChange={e => s("deadline",e.target.value)}
            style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: C.rs, padding: "13px 16px", color: form.deadline ? C.white : C.faint, fontSize: 15, fontFamily: "Outfit", outline: "none", cursor: "pointer", display: "block" }} />
        </div>
      </div>
      <Inp label="Sted" value={form.location} onChange={e => s("location",e.target.value)} />
      <div style={{ background: C.bg, border: `1px solid ${form.isBoardMeeting ? C.gold+"60" : C.border}`, borderRadius: C.rs, padding: "14px 16px", marginBottom: 16, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div>
          <div style={{ fontSize: 15, color: C.white, fontWeight: 600 }}>🏛️ Bestyrelsesmøde</div>
          <div style={{ fontSize: 12, color: C.soft, marginTop: 2 }}>Kun synligt for bestyrelsesmedlemmer</div>
        </div>
        <button onClick={() => s("isBoardMeeting", !form.isBoardMeeting)} style={{
          background: form.isBoardMeeting ? C.gold : "transparent",
          border: `2px solid ${form.isBoardMeeting ? C.gold : C.border}`,
          borderRadius: 20, width: 52, height: 28, cursor: "pointer", position: "relative", transition: "all 0.2s", flexShrink: 0,
        }}>
          <div style={{ width: 20, height: 20, borderRadius: "50%", background: C.white, position: "absolute", top: 2, left: form.isBoardMeeting ? 28 : 4, transition: "left 0.2s" }} />
        </button>
      </div>
      <div style={{ background: C.bg, border: `1px solid ${form.noAttendance ? C.yellow+"60" : C.border}`, borderRadius: C.rs, padding: "14px 16px", marginBottom: 16, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div>
          <div style={{ fontSize: 15, color: C.white, fontWeight: 600 }}>Ikke mødepligt</div>
          <div style={{ fontSize: 12, color: C.soft, marginTop: 2 }}>Mødet tæller ikke i fremmødestatistik</div>
        </div>
        <button onClick={() => s("noAttendance", !form.noAttendance)} style={{
          background: form.noAttendance ? C.yellow : "transparent",
          border: `2px solid ${form.noAttendance ? C.yellow : C.border}`,
          borderRadius: 20, width: 52, height: 28, cursor: "pointer", position: "relative", transition: "all 0.2s", flexShrink: 0,
        }}>
          <div style={{ width: 20, height: 20, borderRadius: "50%", background: C.white, position: "absolute", top: 2, left: form.noAttendance ? 28 : 4, transition: "left 0.2s" }} />
        </button>
      </div>
      <Txa label="Beskrivelse" value={form.description} onChange={e => s("description",e.target.value)} />
      <Txa label="Dagsorden" value={form.agenda} onChange={e => s("agenda",e.target.value)} style={{ minHeight: 100 }} />
      <div style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: C.rs, padding: 16, marginBottom: 16 }}>
        <Label>Roller</Label>
        {form.isBoardMeeting ? (
          <Sel label="Ansvarlig" value={form.responsible1} onChange={e => s("responsible1", e.target.value ? parseInt(e.target.value) : null)}>
            <option value="">– Vælg –</option>
            {members.filter(m => m.board || m.sysRole === "admin").map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
          </Sel>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            {[["responsible1","Ansvarlig 1"],["responsible2","Ansvarlig 2"],["boedemester","Bødemester"],["treMinutter","3 Minutter"]].map(([k,lbl]) => (
              <Sel key={k} label={lbl} value={form[k]} onChange={e => s(k, e.target.value ? parseInt(e.target.value) : null)}>
                <option value="">– Vælg –</option>
                {members.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
              </Sel>
            ))}
          </div>
        )}
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div>
          {currentMode === "edit" && !confirmDelete && (
            <SmBtn v="danger" onClick={() => setConfirmDelete(true)}>✕ Slet møde</SmBtn>
          )}
          {currentMode === "edit" && confirmDelete && (
            <div style={{ background: C.red+"15", border: `1px solid ${C.red}50`, borderRadius: C.rs, padding: "10px 14px" }}>
              <div style={{ fontSize: 13, color: C.white, marginBottom: 8 }}>Er du sikker? Dette kan ikke fortrydes.</div>
              <div style={{ display: "flex", gap: 8 }}>
                <SmBtn v="ghost" onClick={() => setConfirmDelete(false)}>Annullér</SmBtn>
                <SmBtn v="danger" onClick={() => onDelete(form.id)}>✕ Ja, slet møde</SmBtn>
              </div>
            </div>
          )}
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <SmBtn v="ghost" onClick={onClose}>Annullér</SmBtn>
          <Btn onClick={() => onSave(form)}>Gem møde</Btn>
        </div>
      </div>
    </Modal>
  );
};

// ─── MEMBER MODAL ─────────────────────────────────────────────────────────────
const MemberModal = ({ mode, member, onClose, onSave, isOwnProfile }) => {
  const blank = { name:"", email:"", phone:"", address:"", birthday:"", sysRole:"member", clubRole:"Medlem", board:false, password:"rt30x6!", mustChangePw:true };
  const [form, setForm] = useState(mode === "new" ? blank : { ...member, birthday: member?.birthday || "" });
  const s = (k, v) => setForm(f => ({ ...f, [k]: v }));
  return (
    <Modal title={mode === "new" ? "Nyt medlem" : "Redigér medlem"} onClose={onClose}>
      <Inp label="Fulde navn" value={form.name} onChange={e => s("name", e.target.value)} />
      <Inp label="Email" type="email" value={form.email||""} onChange={e => s("email", e.target.value)} />
      <Inp label="Telefon" value={form.phone||""} onChange={e => s("phone", e.target.value)} />
      <Inp label="Adresse" value={form.address||""} onChange={e => s("address", e.target.value)} />

      <div style={{ marginBottom: 16 }}>
        <Label>🎂 Fødselsdag</Label>
        <input type="date" value={form.birthday||""} onChange={e => s("birthday", e.target.value)}
          style={{ width: "100%", background: C.bg, border: `1px solid ${C.border}`, borderRadius: C.rs, padding: "13px 16px", color: form.birthday ? C.white : C.faint, fontSize: 15, fontFamily: "Outfit", outline: "none", boxSizing: "border-box" }} />
      </div>

      {!isOwnProfile && <>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <Sel label="Systemrolle" value={form.sysRole} onChange={e => s("sysRole", e.target.value)}>
          <option value="member">Medlem</option>
          <option value="admin">Administrator</option>
        </Sel>
        <Sel label="Klubrolle" value={form.clubRole} onChange={e => s("clubRole", e.target.value)}>
          {CLUB_ROLES.map(r => <option key={r} value={r}>{r}</option>)}
        </Sel>
      </div>

      {/* I bestyrelsen */}
      <div style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: C.rs, padding: "14px 16px", marginBottom: 16, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <span style={{ fontSize: 15, color: C.white }}>I bestyrelsen</span>
        <button onClick={() => s("board", !form.board)} style={{
          background: form.board ? C.gold : "transparent",
          border: `2px solid ${form.board ? C.gold : C.border}`,
          borderRadius: 20, width: 52, height: 28, cursor: "pointer",
          position: "relative", transition: "all 0.2s",
        }}>
          <div style={{
            width: 20, height: 20, borderRadius: "50%", background: C.white,
            position: "absolute", top: 2, left: form.board ? 28 : 4,
            transition: "left 0.2s",
          }} />
        </button>
      </div>
      </>}

      <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
        <SmBtn v="ghost" onClick={onClose}>Annullér</SmBtn>
        <Btn disabled={!form.name} onClick={() => onSave(form)}>Gem</Btn>
      </div>
    </Modal>
  );
};

// ─── STATS MEMBER DETAIL MODAL ────────────────────────────────────────────────
const StatsMemberModal = ({ member, meetings, onClose }) => {
  const pastAll = meetings.filter(m => isPast(m.date)).sort((a, b) => b.date.localeCompare(a.date));
  const getStatus = (m) => {
    if (m.attendees.includes(member.id)) return { label: "Tilmeldt", col: C.green };
    if (m.absent.includes(member.id))   return { label: "Afbud",    col: C.red };
    return { label: "Ikke svaret", col: C.soft };
  };
  return (
    <Modal title={member.name} onClose={onClose} tall>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
        <Avatar member={member} size={52} />
        <div>
          <div style={{ fontSize: 12, color: C.gold, fontWeight: 600, letterSpacing: "0.1em" }}>{member.clubRole}</div>
          <div style={{ fontSize: 14, color: C.soft }}>Fremmøde – klubår</div>
        </div>
        <div style={{ marginLeft: "auto" }}>
          <PctRing pct={memberAttPct(member.id, meetings)} size={64} stroke={5} />
        </div>
      </div>

      {pastAll.length === 0 && <div style={{ color: C.faint, fontSize: 14 }}>Ingen afholdte møder endnu.</div>}

      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {pastAll.map(m => {
          const { label, col } = getStatus(m);
          return (
            <div key={m.id} style={{ background: C.bg, border: `1px solid ${col}25`, borderRadius: C.rs, padding: "14px 16px" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
                <div style={{ fontWeight: 600, color: C.white, fontSize: 15 }}>{m.title}</div>
                <span style={{ background: col + "20", color: col, border: `1px solid ${col}40`, padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 700 }}>{label}</span>
              </div>
              <div style={{ fontSize: 13, color: C.soft }}>{fmtShort(m.date)} · {m.location}</div>
              {m.description && <div style={{ fontSize: 12, color: C.faint, marginTop: 4 }}>{m.description}</div>}
            </div>
          );
        })}
      </div>

      <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}>
        <SmBtn v="ghost" onClick={onClose}>Luk</SmBtn>
      </div>
    </Modal>
  );
};

// ─── STATISTICS TAB ───────────────────────────────────────────────────────────
// ─── PDF GENERATOR ────────────────────────────────────────────────────────────
function generatePDF({ members, meetings, selectedYear, yearStart, yearEnd, cyMeetings, cyMeetingsAll, memberFinesYear, grandTotal }) {
  const lines = [];
  const yr = `${selectedYear}/${selectedYear+1}`;

  lines.push(`RT30X6 – Årsrapport ${yr}`);
  lines.push(`Genereret: ${new Date().toLocaleDateString("da-DK")}`);
  lines.push(`Klubår: 1. maj ${selectedYear} – 30. april ${selectedYear+1}`);
  lines.push(``);

  // MØDER
  lines.push(`══════════════════════════════`);
  lines.push(`MØDER (${cyMeetingsAll.length} stk.)`);
  lines.push(`══════════════════════════════`);
  cyMeetingsAll.sort((a,b) => a.date.localeCompare(b.date)).forEach(m => {
    lines.push(``);
    lines.push(`${fmtFull(m.date)} kl. ${m.time} – ${m.title}`);
    if (m.subtitle) lines.push(`  Undertitel: ${m.subtitle}`);
    if (m.location) lines.push(`  Sted: ${m.location}`);
    if (m.noAttendance) lines.push(`  ★ Ikke mødepligt`);
    lines.push(`  Fremmøde: ${m.attendees.length}/${members.length} (${meetingAttPct(m, members.length)}%)`);
    // Tilmeldte
    if (m.attendees.length > 0) {
      lines.push(`  Tilmeldte (${m.attendees.length}):`);
      m.attendees.forEach(id => { const mn = members.find(x => x.id === id); if (mn) lines.push(`    ✓ ${mn.name}`); });
    }
    // Afbud
    if (m.absent.length > 0) {
      lines.push(`  Afbud (${m.absent.length}):`);
      m.absent.forEach(id => { const mn = members.find(x => x.id === id); if (mn) lines.push(`    ✗ ${mn.name}`); });
    }
    // Ikke svaret
    const unanswered = members.filter(mn => !m.attendees.includes(mn.id) && !m.absent.includes(mn.id));
    if (unanswered.length > 0) {
      lines.push(`  Ikke svaret (${unanswered.length}):`);
      unanswered.forEach(mn => lines.push(`    ? ${mn.name}`));
    }
    // Ansvarlige
    const resp = [m.responsible1, m.responsible2].filter(Boolean).map(id => members.find(x => x.id === id)?.name).filter(Boolean);
    if (resp.length) lines.push(`  Ansvarlige: ${resp.join(" & ")}`);
    const bm = members.find(x => x.id === m.boedemester);
    if (bm) lines.push(`  Bødemester: ${bm.name}`);
    const tre = members.find(x => x.id === m.treMinutter);
    if (tre) lines.push(`  3 Minutter: ${tre.name}`);
    const fines = (m.fines||[]).filter(f => f.amount);
    if (fines.length) {
      lines.push(`  Bøder:`);
      fines.forEach(f => {
        const mn = members.find(x => x.id === f.memberId);
        lines.push(`    – ${mn?.name||"?"}: ${f.amount} kr (${f.note||""})`);
      });
    }
  });

  lines.push(``);
  lines.push(`══════════════════════════════`);
  lines.push(`FREMMØDESTATISTIK`);
  lines.push(`══════════════════════════════`);
  const statMeetings = cyMeetings;
  [...members].sort((a,b) => {
    const pa = statMeetings.length ? Math.round(statMeetings.filter(m=>m.attendees.includes(a.id)).length/statMeetings.length*100) : 0;
    const pb = statMeetings.length ? Math.round(statMeetings.filter(m=>m.attendees.includes(b.id)).length/statMeetings.length*100) : 0;
    return pb - pa;
  }).forEach(m => {
    const att = statMeetings.filter(mt => mt.attendees.includes(m.id)).length;
    const pct = statMeetings.length ? Math.round(att/statMeetings.length*100) : 0;
    lines.push(`  ${m.name.padEnd(30)} ${att}/${statMeetings.length} møder (${pct}%)`);
  });

  lines.push(``);
  lines.push(`══════════════════════════════`);
  lines.push(`BØDEOVERSIGT – Total: ${grandTotal} kr`);
  lines.push(`══════════════════════════════`);
  if (memberFinesYear.length === 0) {
    lines.push(`  Ingen bøder dette klubår`);
  } else {
    memberFinesYear.forEach(({ member, total, details }) => {
      lines.push(`  ${member.name}: ${total} kr`);
      details.forEach(d => lines.push(`    – ${d.meetingTitle} (${fmtShort(d.meetingDate)}): ${d.amount} kr – ${d.note||""}`));
    });
  }

  lines.push(``);
  lines.push(`══════════════════════════════`);
  lines.push(`MEDLEMSLISTE`);
  lines.push(`══════════════════════════════`);
  [...members].sort((a,b) => a.name.localeCompare(b.name)).forEach(m => {
    lines.push(`  ${m.name}`);
    if (m.clubRole) lines.push(`    Rolle: ${m.clubRole}`);
    if (m.phone)   lines.push(`    Tlf: ${m.phone}`);
    if (m.email)   lines.push(`    Email: ${m.email}`);
    if (m.address) lines.push(`    Adresse: ${m.address}`);
    if (m.birthday) lines.push(`    Fødselsdag: ${fmtShort(m.birthday)}`);
  });

  // Download som tekstfil
  const blob = new Blob([lines.join("\n")], { type: "text/plain;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `RT30X6-årsrapport-${yr}.txt`;
  a.click();
  URL.revokeObjectURL(url);
}

const StatsTab = ({ members, meetings }) => {
  const [view, setView]               = useState("members");
  const [detailMeeting, setDetailMeeting] = useState(null);
  const [detailMember, setDetailMember]   = useState(null);

  // ── Year selector ──────────────────────────────────────────────────────────
  // Build list of all club years that have meetings
  const allYears = (() => {
    const years = new Set();
    meetings.filter(m => isPast(m.date)).forEach(m => {
      const d = new Date(m.date);
      const yr = d.getMonth() >= 4 ? d.getFullYear() : d.getFullYear() - 1;
      years.add(yr);
    });
    const currentYr = CY_START.getFullYear();
    years.add(currentYr);
    return [...years].sort((a, b) => b - a);
  })();

  const [selectedYear, setSelectedYear] = useState(CY_START.getFullYear());
  const yearStart = new Date(selectedYear, 4, 1); // May 1
  const yearEnd   = new Date(selectedYear + 1, 3, 30); // April 30

  const cyMeetings = meetings.filter(m =>
    isPast(m.date) && new Date(m.date) >= yearStart && new Date(m.date) <= yearEnd && !m.noAttendance
  );
  const cyMeetingsAll = meetings.filter(m =>
    isPast(m.date) && new Date(m.date) >= yearStart && new Date(m.date) <= yearEnd
  );

  const totalM = members.length;

  const memberAttPctYear = (memberId) => {
    if (!cyMeetings.length) return null;
    return Math.round((cyMeetings.filter(m => m.attendees.includes(memberId)).length / cyMeetings.length) * 100);
  };

  const memberStats = members.map(m => ({
    member: m, pct: memberAttPctYear(m.id),
    attended: cyMeetings.filter(mt => mt.attendees.includes(m.id)).length,
    total: cyMeetings.length,
  })).sort((a, b) => (b.pct ?? -1) - (a.pct ?? -1));

  const meetingStats = cyMeetingsAll.map(m => ({
    meeting: m, pct: meetingAttPct(m, totalM), att: m.attendees.length, abs: m.absent.length,
  })).sort((a, b) => b.pct - a.pct);

  // Fines for selected year
  const memberFinesYear = members.map(member => {
    const total = cyMeetingsAll.reduce((sum, mtg) => {
      return sum + (mtg.fines || []).filter(f => f.memberId === member.id && f.amount)
        .reduce((s, f) => s + (parseFloat(f.amount) || 0), 0);
    }, 0);
    const details = cyMeetingsAll.flatMap(mtg =>
      (mtg.fines || []).filter(f => f.memberId === member.id && f.amount)
        .map(f => ({ ...f, meetingTitle: mtg.title, meetingDate: mtg.date }))
    );
    return { member, total, details };
  }).filter(x => x.total > 0).sort((a, b) => b.total - a.total);
  const grandTotal = memberFinesYear.reduce((s, x) => s + x.total, 0);

  // Meeting detail modal
  const MeetingDetailModal = ({ meeting, onClose }) => {
    const unanswered = members.filter(m => !meeting.attendees.includes(m.id) && !meeting.absent.includes(m.id));
    return (
      <Modal title={`${meeting.title} – ${meeting.subtitle}`} onClose={onClose}>
        <div style={{ fontSize: 13, color: C.soft, marginBottom: 20 }}>{fmtFull(meeting.date)} · kl. {meeting.time}</div>
        {[["Tilmeldte", meeting.attendees, C.green],["Afbud", meeting.absent, C.red],["Ikke svaret", unanswered.map(m => m.id), C.soft]].map(([lbl, ids, col]) => (
          <div key={lbl} style={{ background: C.bg, border: `1px solid ${col}25`, borderRadius: C.rs, padding: 14, marginBottom: 12 }}>
            <div style={{ fontSize: 11, color: col, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 10 }}>{lbl} ({ids.length})</div>
            {ids.map(id => { const m = getMember(members, id); return m ? (
              <div key={id} style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                <Avatar member={m} size={28}/><span style={{ fontSize: 14, color: C.white }}>{m.name}</span>
              </div>
            ) : null; })}
            {ids.length === 0 && <div style={{ fontSize: 13, color: C.faint }}>Ingen</div>}
          </div>
        ))}
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <SmBtn v="ghost" onClick={onClose}>Luk</SmBtn>
        </div>
      </Modal>
    );
  };

  const TabBtn = ({ id, label }) => (
    <button onClick={() => setView(id)} style={{
      flex: 1, background: view === id ? C.gold+"18" : "transparent",
      color: view === id ? C.gold : C.soft,
      border: view === id ? `1px solid ${C.gold}50` : `1px solid ${C.border}`,
      borderRadius: C.rs, padding: "10px 6px", fontSize: 13, fontWeight: 600,
      fontFamily: "Outfit", cursor: "pointer", transition: "all 0.15s",
    }}>{label}</button>
  );

  return (
    <div>
      {detailMeeting && <MeetingDetailModal meeting={detailMeeting} onClose={() => setDetailMeeting(null)} />}
      {detailMember  && <StatsMemberModal member={detailMember} meetings={meetings} onClose={() => setDetailMember(null)} />}

      <div style={{ marginBottom: 20 }}>
        <h1 style={{ margin: "0 0 12px", fontFamily: "Outfit", fontSize: 36, fontWeight: 900, color: C.white }}>Statistik</h1>

        {/* Year selector */}
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 8 }}>
          {allYears.map(yr => (
            <button key={yr} onClick={() => setSelectedYear(yr)} style={{
              background: selectedYear === yr ? C.gold : "transparent",
              color: selectedYear === yr ? C.bg : C.soft,
              border: `1px solid ${selectedYear === yr ? C.gold : C.border}`,
              borderRadius: 20, padding: "5px 14px", fontSize: 13, fontWeight: 600,
              cursor: "pointer", fontFamily: "Outfit",
            }}>
              {yr}/{yr+1}
            </button>
          ))}
        </div>
        <div style={{ fontSize: 13, color: C.soft }}>
          1. maj {selectedYear} – 30. april {selectedYear+1} · {cyMeetings.length} møder med mødepligt
        </div>
      </div>

      <div style={{ display: "flex", gap: 8, marginBottom: 20 }}>
        <TabBtn id="members" label="👤 Medlemmer" />
        <TabBtn id="meetings" label="📅 Fremmøde" />
        <TabBtn id="fines" label="⚖️ Bøder" />
      </div>

      {/* Members */}
      {view === "members" && (
        <>
          <div style={{ display: "flex", gap: 18, marginBottom: 16 }}>
            {[[C.green,"70–100 %"],[C.yellow,"51–69 %"],[C.red,"0–50 %"]].map(([col, lbl]) => (
              <div key={lbl} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: C.soft }}>
                <div style={{ width: 10, height: 10, borderRadius: "50%", background: col, flexShrink: 0 }} />{lbl}
              </div>
            ))}
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {memberStats.map(({ member, pct, attended, total }, i) => (
              <div key={member.id} onClick={() => setDetailMember(member)}
                style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: C.rs, padding: "16px 18px", display: "flex", alignItems: "center", gap: 16, cursor: "pointer", transition: "border-color 0.15s" }}
                onMouseEnter={e => e.currentTarget.style.borderColor = C.borderHi}
                onMouseLeave={e => e.currentTarget.style.borderColor = C.border}
              >
                <div style={{ fontSize: 14, color: C.faint, fontWeight: 700, minWidth: 24, textAlign: "center" }}>#{i+1}</div>
                <Avatar member={member} size={46} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 16, fontWeight: 600, color: C.white, marginBottom: 4, display: "flex", alignItems: "center", gap: 6 }}>
                    {pct === 100 && <span style={{ fontSize: 18 }}>🥇</span>}
                    {member.name}
                  </div>
                  <div style={{ height: 6, background: C.bg, borderRadius: 3, overflow: "hidden" }}>
                    <div style={{ height: "100%", width: (pct??0)+"%", background: pct !== null ? pctColor(pct) : C.faint, borderRadius: 3 }} />
                  </div>
                  <div style={{ fontSize: 12, color: C.soft, marginTop: 4 }}>{attended}/{total} møder</div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <PctRing pct={pct} size={64} stroke={5} />
                  <span style={{ fontSize: 18, color: C.faint }}>›</span>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Meetings */}
      {view === "meetings" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {meetingStats.map(({ meeting, pct, att, abs }, i) => {
            const unans = totalM - att - abs;
            return (
              <div key={meeting.id} onClick={() => setDetailMeeting(meeting)}
                style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: C.rs, padding: "16px 18px", display: "flex", alignItems: "center", gap: 16, cursor: "pointer", transition: "border-color 0.15s" }}
                onMouseEnter={e => e.currentTarget.style.borderColor = C.borderHi}
                onMouseLeave={e => e.currentTarget.style.borderColor = C.border}
              >
                <div style={{ fontSize: 14, color: C.faint, fontWeight: 700, minWidth: 24, textAlign: "center" }}>#{i+1}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 16, fontWeight: 600, color: C.white, marginBottom: 4 }}>{meeting.title} – {meeting.subtitle}</div>
                  <div style={{ height: 6, background: C.bg, borderRadius: 3, overflow: "hidden", marginBottom: 4 }}>
                    <div style={{ height: "100%", width: pct+"%", background: pctColor(pct), borderRadius: 3 }} />
                  </div>
                  <div style={{ display: "flex", gap: 14, fontSize: 12 }}>
                    <span style={{ color: C.green }}>{att} tilmeldt</span>
                    <span style={{ color: C.red }}>{abs} afbud</span>
                    <span style={{ color: C.soft }}>{unans} ubesvaret</span>
                    <span style={{ color: C.faint }}>{fmtShort(meeting.date)}</span>
                  </div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <PctRing pct={pct} size={64} stroke={5} />
                  <span style={{ fontSize: 18, color: C.faint }}>›</span>
                </div>
              </div>
            );
          })}
          {meetingStats.length === 0 && <div style={{ color: C.faint, fontSize: 15 }}>Ingen møder dette klubår.</div>}
        </div>
      )}

      {/* Fines */}
      {view === "fines" && (
        <>
          <div style={{ fontSize: 14, color: C.soft, marginBottom: 16 }}>
            Total: <span style={{ color: C.gold, fontWeight: 700 }}>{grandTotal} kr</span>
          </div>
          {memberFinesYear.length === 0 ? (
            <div style={{ color: C.faint, fontSize: 15, textAlign: "center", marginTop: 40 }}>Ingen bøder dette klubår 🎉</div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {memberFinesYear.map(({ member, total, details }) => (
                <FineRow key={member.id} member={member} total={total} details={details} />
              ))}
            </div>
          )}
        </>
      )}

      {/* PDF RAPPORT KNAP */}
      <div style={{ marginTop: 32, paddingTop: 20, borderTop: `1px solid ${C.border}` }}>
        <Btn onClick={() => generatePDF({ members, meetings, selectedYear, yearStart, yearEnd, cyMeetings, cyMeetingsAll, memberFinesYear, grandTotal })}
          style={{ width: "100%", justifyContent: "center" }}>
          📄 Download årsrapport {selectedYear}/{selectedYear+1}
        </Btn>
      </div>
    </div>
  );
};

// ─── NEXT BIRTHDAY ────────────────────────────────────────────────────────────
const nextBirthday = (members) => {
  const now = TODAY;
  const withDays = members
    .filter(m => m.birthday)
    .map(m => {
      const bd = new Date(m.birthday);
      // Next occurrence this year or next
      let next = new Date(now.getFullYear(), bd.getMonth(), bd.getDate());
      if (next < now) next = new Date(now.getFullYear() + 1, bd.getMonth(), bd.getDate());
      const days = Math.ceil((next - now) / 86400000);
      return { member: m, next, days };
    })
    .sort((a, b) => a.days - b.days);
  return withDays[0] || null;
};

const fmtBirthday = (bd) => {
  const d = new Date(bd);
  return d.toLocaleDateString("da-DK", { day: "numeric", month: "long" });
};
const downloadICS = (meeting) => {
  const dt = meeting.date.replace(/-/g, "") + "T" + meeting.time.replace(":", "") + "00";
  const endH = String(parseInt(meeting.time.split(":")[0]) + 2).padStart(2, "0");
  const dtEnd = meeting.date.replace(/-/g, "") + "T" + endH + meeting.time.split(":")[1] + "00";
  const ics = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//Round Table 30X6//DA",
    "BEGIN:VEVENT",
    `DTSTART:${dt}`,
    `DTEND:${dtEnd}`,
    `SUMMARY:RT30X6 – ${meeting.title} – ${meeting.subtitle}`,
    `LOCATION:${meeting.location || ""}`,
    `DESCRIPTION:${(meeting.description || "").replace(/\n/g, "\\n")}`,
    "END:VEVENT",
    "END:VCALENDAR",
  ].join("\r\n");

  // iOS webapp mode kan ikke downloade filer — brug data URI direkte i samme vindue
  const isIOSWebapp = navigator.standalone === true;
  if (isIOSWebapp) {
    const dataUri = "data:text/calendar;charset=utf8," + encodeURIComponent(ics);
    window.location.href = dataUri;
  } else {
    const blob = new Blob([ics], { type: "text/calendar;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${meeting.title}.ics`;
    document.body.appendChild(a);
    a.click();
    setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 100);
  }
};

// ─── CHANGE PASSWORD MODAL (for admin setting others' passwords) ──────────────
const ChangePasswordModal = ({ member, onClose, onSave }) => {
  const [pw, setPw]   = useState("");
  const [pw2, setPw2] = useState("");
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);
  const go = async () => {
    if (pw.length < 6) return setErr("Min. 6 tegn");
    if (pw !== pw2)    return setErr("Matcher ikke");
    setSaving(true);
    await onSave({ ...member, password: pw, mustChangePw: true });
    setSaving(false);
    onClose();
  };
  const resetToDefault = async () => {
    setSaving(true);
    await onSave({ ...member, password: "rt30x6!", mustChangePw: true });
    setSaving(false);
    onClose();
  };
  return (
    <Modal title={`Nulstil adgangskode – ${member.name}`} onClose={onClose}>
      {err && <div style={{ color: C.red, fontSize: 13, marginBottom: 12 }}>{err}</div>}
      <Inp label="Ny adgangskode" type="password" value={pw} onChange={e => setPw(e.target.value)} placeholder="Mindst 6 tegn" />
      <Inp label="Gentag" type="password" value={pw2} onChange={e => setPw2(e.target.value)} placeholder="••••••••" onKeyDown={e => e.key === "Enter" && go()} />
      <div style={{ fontSize: 12, color: C.soft, marginBottom: 16 }}>Brugeren skal skifte adgangskode ved næste login.</div>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
        <SmBtn v="ghost" onClick={resetToDefault} disabled={saving} style={{ color: C.yellow, borderColor: C.yellow+"40" }}>
          {saving ? "Gemmer…" : "Nulstil til standardkode"}
        </SmBtn>
        <div style={{ display: "flex", gap: 10 }}>
          <SmBtn v="ghost" onClick={onClose} disabled={saving}>Annullér</SmBtn>
          <Btn onClick={go} disabled={saving}>{saving ? "Gemmer…" : "Gem adgangskode"}</Btn>
        </div>
      </div>
    </Modal>
  );
};

// ─── CHAT TAB ────────────────────────────────────────────────────────────────
const INIT_MESSAGES = [];

const ChatTab = ({ user, members, messages, notifications, unreadChat, unreadNotifs, onSend, onClearChatBadge, onClearNotifBadge }) => {
  const [tab, setTab]   = useState("chat");
  const [text, setText] = useState("");
  const bottomRef = useRef(null);
  useEffect(() => { if (tab === "chat") bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, tab]);

  const send = () => {
    if (!text.trim()) return;
    onSend(text.trim());
    setText("");
  };

  const fmtTs    = (ts) => new Date(ts).toLocaleString("da-DK", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
  const fmtTsFull = (ts) => new Date(ts).toLocaleString("da-DK", { weekday: "short", day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });

  const typeLabel = { manual: "✍️ Manuel", remind7: "📅 7 dage", today: "🔔 I dag", deadline: "⏰ Frist" };

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "calc(100vh - 180px)" }}>
      <div style={{ marginBottom: 16 }}>
        <h1 style={{ margin: "0 0 12px", fontFamily: "Outfit", fontSize: 36, fontWeight: 900, color: C.white }}>Beskeder</h1>
        <div style={{ display: "flex", gap: 0, border: `1px solid ${C.border}`, borderRadius: C.rs, overflow: "hidden" }}>
          {[["chat","💬 Chat", unreadChat],["notifs","📣 Notifikationer", unreadNotifs]].map(([id,lbl,badge]) => (
            <button key={id} onClick={() => {
              setTab(id);
              if (id==="notifs") onClearNotifBadge?.();
              if (id==="chat") onClearChatBadge?.();
            }} style={{
              flex: 1, background: tab===id ? C.gold+"22" : "transparent",
              borderLeft: id==="notifs" ? `1px solid ${C.border}` : "none",
              border: "none", borderLeft: id==="notifs" ? `1px solid ${C.border}` : "none",
              padding: "12px 8px", color: tab===id ? C.gold : C.soft,
              fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: "Outfit",
              display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
            }}>
              {lbl}
              {badge > 0 && (
                <span style={{ background: C.red, color: "#fff", borderRadius: 10, padding: "2px 8px", fontSize: 12, fontWeight: 700 }}>
                  {badge > 9 ? "9+" : badge}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* ── CHAT ── */}
      {tab === "chat" && <>
        <div style={{ flex: 1, overflowY: "auto", display: "flex", flexDirection: "column", gap: 12, paddingBottom: 8 }}>
          {messages.length === 0 && <div style={{ color: C.faint, fontSize: 14, textAlign: "center", marginTop: 40 }}>Ingen beskeder endnu. Vær den første!</div>}
          {messages.map(msg => {
            const m   = getMember(members, msg.memberId);
            const own = msg.memberId === user.id;
            return (
              <div key={msg.id} style={{ display: "flex", gap: 10, flexDirection: own ? "row-reverse" : "row", alignItems: "flex-end" }}>
                {!own && <Avatar member={m || { initials: "?" }} size={32} />}
                <div style={{ maxWidth: "72%", display: "flex", flexDirection: "column", alignItems: own ? "flex-end" : "flex-start" }}>
                  {!own && <div style={{ fontSize: 11, color: C.soft, marginBottom: 3 }}>{m?.name || "?"}</div>}
                  <div style={{
                    background: own ? C.gold : C.surface, color: own ? "#08090b" : C.white,
                    borderRadius: own ? "16px 16px 4px 16px" : "16px 16px 16px 4px",
                    padding: "10px 14px", fontSize: 15, lineHeight: 1.5,
                    border: own ? "none" : `1px solid ${C.border}`,
                  }}>{msg.text}</div>
                  <div style={{ fontSize: 10, color: C.faint, marginTop: 3 }}>{fmtTs(msg.ts)}</div>
                </div>
              </div>
            );
          })}
          <div ref={bottomRef} />
        </div>
        <div style={{ display: "flex", gap: 10, paddingTop: 12, borderTop: `1px solid ${C.border}` }}>
          <input value={text} onChange={e => setText(e.target.value)} onKeyDown={e => e.key === "Enter" && !e.shiftKey && send()}
            placeholder="Skriv en besked…" style={{
              flex: 1, background: C.surface, border: `1px solid ${C.border}`, borderRadius: C.r,
              padding: "13px 16px", color: C.white, fontSize: 15, fontFamily: "Outfit", outline: "none",
            }} />
          <Btn onClick={send} style={{ minHeight: 48, padding: "12px 18px" }}>Send</Btn>
        </div>
      </>}

      {/* ── NOTIFICATIONS ── */}
      {tab === "notifs" && (
        <div style={{ flex: 1, overflowY: "auto", display: "flex", flexDirection: "column", gap: 10 }}>
          {notifications.length === 0 && <div style={{ color: C.faint, fontSize: 14, textAlign: "center", marginTop: 40 }}>Ingen notifikationer sendt endnu.</div>}
          {[...notifications].sort((a,b) => b.ts.localeCompare(a.ts)).map((n, i) => {
            const sender = getMember(members, n.sentBy);
            return (
              <div key={i} style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: C.rs, padding: "14px 16px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10, marginBottom: 6 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: C.white }}>{n.title}</div>
                  <div style={{ fontSize: 11, color: C.faint, flexShrink: 0 }}>{fmtTsFull(n.ts)}</div>
                </div>
                <div style={{ fontSize: 14, color: C.soft, marginBottom: 8 }}>{n.body}</div>
                <div style={{ fontSize: 11, color: C.faint }}>
                  Sendt af {sender?.name || "?"} · {typeLabel[n.type] || "📣 Notifikation"}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ─── RSVP LOG MODAL ───────────────────────────────────────────────────────────
const RsvpLogModal = ({ meeting, members, onClose }) => {
  const log = [...(meeting.rsvpLog || [])].sort((a, b) => new Date(b.ts) - new Date(a.ts));
  const fmtTs = (ts) => new Date(ts).toLocaleString("da-DK", { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
  return (
    <Modal title={`Tilmeldingslog – ${meeting.title}`} onClose={onClose}>
      <div style={{ fontSize: 13, color: C.soft, marginBottom: 16 }}>
        {log.length === 0 ? "Ingen tilmeldinger registreret endnu." : `${log.length} handlinger registreret`}
      </div>
      {log.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {log.map((entry, i) => {
            const m = getMember(members, entry.memberId);
            const isAtt = entry.action === "tilmeldt";
            return (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 12, background: C.bg, border: `1px solid ${isAtt ? C.green+"30" : C.red+"30"}`, borderRadius: C.rs, padding: "10px 14px" }}>
                <Avatar member={m || { initials: "?" }} size={32} />
                <div style={{ flex: 1 }}>
                  <span style={{ color: C.white, fontWeight: 600, fontSize: 14 }}>{m?.name || "Ukendt"}</span>
                  <span style={{ color: isAtt ? C.green : C.red, fontSize: 13, marginLeft: 8, fontWeight: 600 }}>
                    {isAtt ? "✓ Tilmeldt" : "✕ Afmeldt"}
                  </span>
                </div>
                <div style={{ fontSize: 12, color: C.faint, textAlign: "right", flexShrink: 0 }}>{fmtTs(entry.ts)}</div>
              </div>
            );
          })}
        </div>
      )}
      <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 20 }}>
        <SmBtn v="ghost" onClick={onClose}>Luk</SmBtn>
      </div>
    </Modal>
  );
};

// ─── NOTIFY MODAL (admin sends push to all) ───────────────────────────────────
const NotifyModal = ({ meetings, onClose, onSend }) => {
  const upcoming = meetings.filter(m => isUpcoming(m.date)).sort((a,b) => a.date.localeCompare(b.date));
  const [type, setType]     = useState("manual");
  const [text, setText]     = useState("");
  const [meeting, setMeeting] = useState(upcoming[0]?.id || "");
  const [sent, setSent]     = useState(false);

  const templates = {
    manual:   { title: "RT30X6", body: text },
    remind7:  { title: "RT30X6 – Møde om 7 dage", body: upcoming.find(m=>m.id==meeting) ? `${upcoming.find(m=>m.id==meeting).title} – ${fmtShort(upcoming.find(m=>m.id==meeting).date)} kl. ${upcoming.find(m=>m.id==meeting).time}` : "" },
    today:    { title: "RT30X6 – Møde i aften!", body: upcoming.find(m=>m.id==meeting) ? `${upcoming.find(m=>m.id==meeting).title} kl. ${upcoming.find(m=>m.id==meeting).time} på ${upcoming.find(m=>m.id==meeting).location}` : "" },
    deadline: { title: "RT30X6 – Husk tilmelding!", body: upcoming.find(m=>m.id==meeting) ? `Tilmeld dig ${upcoming.find(m=>m.id==meeting).title} – frist snart!` : "" },
  };

  const preview = templates[type] || templates.manual;

  const doSend = async () => {
    await onSend(preview.title, preview.body, type);
    setSent(true);
    setTimeout(onClose, 1500);
  };

  return (
    <Modal title="📣 Send notifikation" onClose={onClose}>
      <div style={{ marginBottom: 16 }}>
        <Label>Type</Label>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
          {[["manual","✍️ Manuel besked"],["remind7","📅 Møde om 7 dage"],["today","🔔 Møde i dag"],["deadline","⏰ Tilmeldingsfrist"]].map(([v,lbl]) => (
            <button key={v} onClick={() => setType(v)} style={{
              background: type===v ? C.gold+"22" : C.bg, border: `1px solid ${type===v ? C.gold : C.border}`,
              borderRadius: C.rs, padding: "10px 12px", color: type===v ? C.gold : C.soft,
              fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "Outfit", textAlign: "left",
            }}>{lbl}</button>
          ))}
        </div>
      </div>

      {type !== "manual" && (
        <div style={{ marginBottom: 16 }}>
          <Label>Møde</Label>
          <select value={meeting} onChange={e => setMeeting(parseInt(e.target.value))}
            style={{ width: "100%", background: C.bg, border: `1px solid ${C.border}`, borderRadius: C.rs, padding: "13px 16px", color: C.white, fontSize: 15, fontFamily: "Outfit", outline: "none" }}>
            {upcoming.map(m => <option key={m.id} value={m.id}>{m.title} – {fmtShort(m.date)}</option>)}
          </select>
        </div>
      )}

      {type === "manual" && (
        <div style={{ marginBottom: 16 }}>
          <Label>Besked</Label>
          <textarea value={text} onChange={e => setText(e.target.value)} rows={3}
            placeholder="Skriv din besked her…"
            style={{ width: "100%", background: C.bg, border: `1px solid ${C.border}`, borderRadius: C.rs, padding: "13px 16px", color: C.white, fontSize: 15, fontFamily: "Outfit", outline: "none", resize: "vertical", boxSizing: "border-box" }} />
        </div>
      )}

      {/* Preview */}
      <div style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: C.rs, padding: 14, marginBottom: 20 }}>
        <div style={{ fontSize: 11, color: C.soft, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 8 }}>Forhåndsvisning</div>
        <div style={{ fontSize: 14, fontWeight: 700, color: C.white, marginBottom: 4 }}>{preview.title}</div>
        <div style={{ fontSize: 13, color: C.soft }}>{preview.body || "–"}</div>
      </div>

      {sent ? (
        <div style={{ textAlign: "center", color: C.green, fontWeight: 600, fontSize: 16, padding: 12 }}>✓ Sendt!</div>
      ) : (
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
          <SmBtn v="ghost" onClick={onClose}>Annullér</SmBtn>
          <Btn onClick={doSend} disabled={type === "manual" && !text.trim()}>📣 Send til alle</Btn>
        </div>
      )}
    </Modal>
  );
};

// ─── FIREBASE SEED (safe — never overwrites existing data) ───────────────────
async function seedFirestoreIfEmpty() {
  const { getDocs } = await import("firebase/firestore");

  // Check if already seeded via a Firestore flag (works across all devices)
  const flagSnap = await getDocs(collection(db, "appconfig"));
  const seeded = flagSnap.docs.some(d => d.id === "seeded");
  if (seeded) return;

  // Only seed members if collection is empty
  const memberSnap = await getDocs(collection(db, "members"));
  if (memberSnap.empty) {
    for (const m of INIT_MEMBERS) {
      await setDoc(doc(db, "members", String(m.id)), m);
    }
  }

  // Only seed meetings if collection is empty
  const meetingSnap = await getDocs(collection(db, "meetings"));
  if (meetingSnap.empty) {
    for (const m of INIT_MEETINGS) {
      await setDoc(doc(db, "meetings", String(m.id)), m);
    }
  }

  // Mark as seeded so this never runs again
  await setDoc(doc(db, "appconfig", "seeded"), { ts: new Date().toISOString() });
}

// ═══════════════════════════════════════════════════════════
// ROOT APP
// ═══════════════════════════════════════════════════════════
export default function App() {
  const [fbUser, setFbUser]     = useState(undefined);
  const [user, setUser]         = useState(null);
  const [members, setMembers]   = useState(INIT_MEMBERS);
  const [meetings, setMeetings] = useState(INIT_MEETINGS);
  const [messages, setMessages] = useState(INIT_MESSAGES);
  const [tab, setTab]           = useState("home");
  const [meetingModal, setMeetingModal]   = useState(null);
  const [memberModal, setMemberModal]     = useState(null);
  const [finesModal, setFinesModal]       = useState(null);
  const [changePwModal, setChangePwModal] = useState(null);
  const [rsvpLogModal, setRsvpLogModal]   = useState(null);
  const [notifyModal, setNotifyModal]     = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadChat, setUnreadChat]       = useState(0);
  const [unreadNotifs, setUnreadNotifs]   = useState(0);
  const [dbReady, setDbReady]             = useState(false);
  const [showAllUp,  setShowAllUp]        = useState(false);
  const [showAllArc, setShowAllArc]       = useState(false);

  // ── Theme ──────────────────────────────────────────────────────────────────
  const systemDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
  const savedTheme = localStorage.getItem("theme");
  const [theme, setTheme] = useState(savedTheme || (systemDark ? "dark" : "light"));
  C = theme === "dark" ? DARK : LIGHT;
  const toggleTheme = () => {
    const next = theme === "dark" ? "light" : "dark";
    setTheme(next);
    localStorage.setItem("theme", next);
  };

  const shownNotifs = useRef({});

  const tabRef = useRef(tab);
  useEffect(() => { tabRef.current = tab; }, [tab]);
  useEffect(() => {
    const keys = Object.keys(localStorage).filter(k => k.startsWith("notif-"));
    keys.forEach(k => { shownNotifs.current[k] = true; });
  }, []);

  // ── Birthday notifications ─────────────────────────────────────────────────
  useEffect(() => {
    if (user && members.length) {
      checkBirthdayNotifications(members, shownNotifs);
    }
  }, [members, user?.id]);

  // Request push permission after login and save token
  useEffect(() => {
    if (!user) return;
    requestPushPermission().then(token => {
      if (token) savePushToken(user.id, token);
    });
    // Listen for foreground push messages
    if (messaging) {
      onMessage(messaging, payload => {
        const { title, body } = payload.notification || {};
        if (title) showLocalNotification(title, body);
      });
    }
  }, [user?.id]);

  // ── Firebase Auth listener ─────────────────────────────────────────────────
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (firebaseUser) => {
      setFbUser(firebaseUser || null);
      if (!firebaseUser) { setUser(null); }
    });
    return unsub;
  }, []);

  // ── Firestore realtime listeners ───────────────────────────────────────────
  useEffect(() => {
    const unsubMembers  = onSnapshot(collection(db, "members"),  snap => {
      const data = snap.docs.map(d => ({ ...d.data(), id: d.data().id || parseInt(d.id) }));
      if (data.length > 0) {
        setMembers(data);
        setDbReady(true);
        // Sync logged-in user — but keep photoURL from local state if Firestore hasn't caught up
        setUser(prev => {
          if (!prev) return prev;
          const fresh = data.find(m => m.id === prev.id);
          if (!fresh) return prev;
          // Keep the most recent photoURL (local state wins if it has one)
          return { ...fresh, photoURL: prev.photoURL || fresh.photoURL };
        });
      }
    });
    const unsubMeetings = onSnapshot(collection(db, "meetings"), snap => {
      const data = snap.docs.map(d => ({ ...d.data(), id: d.data().id || parseInt(d.id) }));
      if (data.length > 0) setMeetings(data);
    });
    const unsubMessages = onSnapshot(query(collection(db, "messages"), orderBy("ts", "asc")), snap => {
      const data = snap.docs.map(d => ({ ...d.data(), id: d.data().id || d.id }));
      setMessages(data);
    });
    const unsubNotifs = onSnapshot(query(collection(db, "notifications"), orderBy("ts", "desc")), snap => {
      const data = snap.docs.map(d => ({ ...d.data(), id: d.id }));
      setNotifications(data);
    });
    return () => { unsubMembers(); unsubMeetings(); unsubMessages(); unsubNotifs(); };
  }, []);

  // ── Recompute unread badges whenever messages/notifications/tab changes ──────
  useEffect(() => {
    if (!user) return;
    const chatLastSeen  = localStorage.getItem("chat-last-seen")  || "0";
    const notifLastSeen = localStorage.getItem("notif-tab-opened-ts") || "0";
    const newChat  = messages.filter(m => m.ts > chatLastSeen  && m.memberId !== user.id).length;
    const newNotif = notifications.filter(n => n.ts > notifLastSeen).length;
    if (tab === "chat") {
      // Clear both when on chat page
      setUnreadChat(0);
      setUnreadNotifs(0);
    } else {
      setUnreadChat(newChat);
      setUnreadNotifs(newNotif);
    }
  }, [messages, notifications, tab, user?.id]);

  // ── Firestore data already seeded - no seed needed ───────────────────────

  // ── Check automatic notifications whenever meetings update ────────────────
  useEffect(() => {
    if (user && meetings.length) {
      checkAutoNotifications(meetings, shownNotifs);
    }
  }, [meetings, user?.id]);

  // ── Listen for notifications pushed by admin ──────────────────────────────
  useEffect(() => {
    if (!user) return;
    const lastSeen = localStorage.getItem("notif-last-seen") || "0";
    const unsub = onSnapshot(collection(db, "notifications"), snap => {
      snap.docChanges().forEach(change => {
        if (change.type === "added") {
          const n = change.doc.data();
          if (n.ts > lastSeen && n.sentBy !== user.id) {
            showLocalNotification(n.title, n.body);
          }
          localStorage.setItem("notif-last-seen", n.ts);
        }
      });
    });
    return unsub;
  }, [user?.id]);

  // ── Loading screen ─────────────────────────────────────────────────────────
  if (fbUser === undefined) return (
    <div style={{ minHeight: "100vh", background: C.bg, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ textAlign: "center" }}>
        <div style={{ fontFamily: "Outfit", fontSize: 32, color: C.gold, marginBottom: 12 }}>RT30X6</div>
        <div style={{ color: C.soft, fontSize: 14 }}>Forbinder…</div>
      </div>
    </div>
  );

  const handleSetPassword = async (updated) => {
    // Used when user changes their OWN password
    await updateDoc(doc(db, "members", String(updated.id)), {
      password: updated.password,
      mustChangePw: false,
    });
    setUser(prev => prev ? { ...prev, password: updated.password, mustChangePw: false } : prev);
  };

  const adminResetPassword = async (updated) => {
    try {
      await setDoc(doc(db, "members", String(updated.id)), {
        password: updated.password,
        mustChangePw: true,
      }, { merge: true });
    } catch (e) {
      alert(`❌ Fejl: ${e.message}`);
    }
  };

  if (!user) return <Login members={members} onLogin={m => { setUser(m); }} onSetPassword={handleSetPassword}
    onFetchFresh={async (id) => {
      const snap = await getDoc(doc(db, "members", String(id)));
      return snap.exists() ? snap.data() : null;
    }} />;

  const isAdmin  = user.sysRole === "admin";
  const isBoardOrAdmin = user.board || isAdmin;
  const upcoming = meetings.filter(m => isUpcoming(m.date) && (!m.isBoardMeeting || isBoardOrAdmin)).sort((a,b) => a.date.localeCompare(b.date));
  const archive  = meetings.filter(m => isPast(m.date) && (!m.isBoardMeeting || isBoardOrAdmin)).sort((a,b) => b.date.localeCompare(a.date));
  const BOTTOM_NAV_H = 90;

  // ── Firestore write helpers ────────────────────────────────────────────────
  const rsvp = async (id, att) => {
    const meeting = meetings.find(m => m.id === id);
    if (!meeting) return;
    const attendees = att ? [...new Set([...meeting.attendees, user.id])] : meeting.attendees.filter(x => x !== user.id);
    const absent    = !att ? [...new Set([...meeting.absent, user.id])] : meeting.absent.filter(x => x !== user.id);
    const logEntry  = { memberId: user.id, action: att ? "tilmeldt" : "afmeldt", ts: new Date().toISOString() };
    const rsvpLog   = [...(meeting.rsvpLog || []), logEntry];
    await updateDoc(doc(db, "meetings", String(id)), { attendees, absent, rsvpLog });
  };

  const saveMeeting = async (data) => {
    if (!data.id) {
      const newId = Date.now();
      await setDoc(doc(db, "meetings", String(newId)), { ...data, id: newId, fines: [], documents: [], rsvpLog: [] });
    } else {
      await setDoc(doc(db, "meetings", String(data.id)), data);
    }
    setMeetingModal(null);
  };

  const deleteMeeting = async (id) => {
    await deleteDoc(doc(db, "meetings", String(id)));
    setMeetingModal(null);
  };

  const saveFines = async (mid, fines) => {
    await updateDoc(doc(db, "meetings", String(mid)), { fines });
    setFinesModal(null);
  };

  const addDocToMeeting = async (mid, docObj) => {
    const meeting = meetings.find(m => m.id === mid);
    if (!meeting) return;
    const documents = [...(meeting.documents || []), docObj];
    await updateDoc(doc(db, "meetings", String(mid)), { documents });
  };

  const saveMember = async (data) => {
    const inits = initials(data.name);
    if (memberModal.mode === "new") {
      const newId = Date.now();
      await setDoc(doc(db, "members", String(newId)), { ...data, id: newId, initials: inits });
    } else {
      const updated = { ...data, initials: inits };
      await updateDoc(doc(db, "members", String(data.id)), updated);
      if (data.id === user.id) setUser(prev => ({ ...prev, ...updated }));
    }
    setMemberModal(null);
  };

  const deleteMember = async (id) => {
    await deleteDoc(doc(db, "members", String(id)));
  };

  const adminRsvp = async (meetingId, memberId, status) => {
    const meeting = meetings.find(m => m.id === meetingId);
    if (!meeting) return;
    let attendees = [...meeting.attendees];
    let absent    = [...meeting.absent];
    // Remove from both lists first
    attendees = attendees.filter(x => x !== memberId);
    absent    = absent.filter(x => x !== memberId);
    // Add to correct list
    if (status === "att") attendees.push(memberId);
    if (status === "abs") absent.push(memberId);
    const logEntry = { memberId, action: status === "att" ? "tilmeldt" : status === "abs" ? "afmeldt" : "ubesvaret", ts: new Date().toISOString(), byAdmin: user.id };
    const rsvpLog  = [...(meeting.rsvpLog || []), logEntry];
    await updateDoc(doc(db, "meetings", String(meetingId)), { attendees, absent, rsvpLog });
  };

  // Send local notification to all members who have the app open
  // (For background push, a server/Cloud Function would be needed)
  const sendPushToAll = async (title, body, type = "manual") => {
    await setDoc(doc(db, "notifications", String(Date.now())), {
      title, body, type, sentBy: user.id, ts: new Date().toISOString()
    });
    showLocalNotification(title, body);
  };

  const sendMessage = async (text) => {
    const newMsg = { memberId: user.id, text, ts: new Date().toISOString(), id: Date.now() };
    await setDoc(doc(db, "messages", String(newMsg.id)), newMsg);
  };

  // ── Bottom nav items ──────────────────────────────────────────────────────
  const totalUnread = unreadChat + unreadNotifs;
  const NAV = [
    { id: "home",     emoji: "🏠", label: "Hjem" },
    { id: "meetings", emoji: "📅", label: "Møder" },
    { id: "members",  emoji: "👥", label: "Medlemmer" },
    { id: "stats",    emoji: "📊", label: "Statistik" },
    { id: "chat",     emoji: "💬", label: "Chat", badge: totalUnread },
    { id: "profile",  emoji: "👤", label: user.name.split(" ")[0] },
  ];

  const handleTabClick = (id) => {
    setTab(id);
    if (id === "chat") {
      // Don't clear here — let the useEffect handle it when tab changes to "chat"
      localStorage.setItem("chat-last-seen", new Date().toISOString());
      localStorage.setItem("notif-tab-opened-ts", new Date().toISOString());
    }
  };

  // Profile "tab" is just a modal-ish inline view
// ─── CHANGELOG ────────────────────────────────────────────────────────────────
const CHANGELOG = [
  { ver: "2.49", items: ["+ Kalender virker nu også når appen er gemt på hjemmeskærmen"] },
  { ver: "2.48", items: ["+ Kalender knap virker nu på alle iPhones"] },
  { ver: "2.47", items: ["Nyt mødetype: Bestyrelsesmøde — kun synligt for bestyrelsesmedlemmer", "Bestyrelsesmøder tæller ikke i fremmødeprocent", "Kun bestyrelsesmedlemmer kan vælges som ansvarlig for bestyrelsesmøder"] },
  { ver: "2.46", items: ["Versionslog er nu kollapsibel", "Årsrapport inkluderer nu komplet deltagerliste per møde"] },
  { ver: "2.45", items: ["Versionslog tilføjet under profil"] },
  { ver: "2.44", items: ["Mødeansvarlige kan nu redigere deres egne møder", "Årsrapport kan downloades under Statistik"] },
  { ver: "2.43", items: ["Alle medlemmer kan nu redigere deres egen profil (navn, email, telefon, adresse, fødselsdag)"] },
];

const ChangelogSection = () => {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ marginTop: 20, background: C.surface, border: `1px solid ${C.border}`, borderRadius: C.r, overflow: "hidden" }}>
      <div onClick={() => setOpen(o => !o)} style={{ padding: "14px 16px", display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer" }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: C.white }}>📋 Versionslog</div>
        <span style={{ color: C.soft, fontSize: 14 }}>{open ? "▲" : "▼"}</span>
      </div>
      {open && CHANGELOG.map(({ ver, items }) => (
        <div key={ver} style={{ padding: "12px 16px", borderTop: `1px solid ${C.border}` }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: C.gold, marginBottom: 6 }}>ver. {ver}</div>
          {items.map((item, i) => (
            <div key={i} style={{ fontSize: 13, color: C.soft, display: "flex", gap: 8, marginBottom: 4 }}>
              <span style={{ color: C.faint }}>•</span>
              <span>{item}</span>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
};

  const ProfileView = () => {
    const fileRef = useRef(null);
    const handlePhoto = (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = (ev) => {
        const img = new Image();
        img.onload = async () => {
          // Compress to 120x120px JPEG — fits in Firestore
          const canvas = document.createElement("canvas");
          const MAX = 120;
          const ratio = Math.min(MAX / img.width, MAX / img.height);
          canvas.width  = Math.round(img.width  * ratio);
          canvas.height = Math.round(img.height * ratio);
          canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
          const photoURL = canvas.toDataURL("image/jpeg", 0.75);
          setUser(prev => ({ ...prev, photoURL })); // update immediately in UI
          // Use updateDoc so we ONLY update photoURL — never overwrite address/birthday etc.
          await updateDoc(doc(db, "members", String(user.id)), { photoURL });
        };
        img.src = ev.target.result;
      };
      reader.readAsDataURL(file);
    };
    return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 28 }}>
        <div style={{ position: "relative" }}>
          <Avatar member={user} size={64} onClick={() => fileRef.current?.click()} />
          <div style={{ position: "absolute", bottom: 0, right: 0, background: C.gold, borderRadius: "50%", width: 22, height: 22, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, cursor: "pointer", border: `2px solid ${C.bg}` }}
            onClick={() => fileRef.current?.click()}>📷</div>
          <input ref={fileRef} type="file" accept="image/*" style={{ display: "none" }} onChange={handlePhoto} />
        </div>
        <div>
          <div style={{ fontFamily: "Outfit", fontSize: 24, fontWeight: 900, color: C.white }}>{user.name}</div>
          <div style={{ fontSize: 14, color: C.gold, marginTop: 2 }}>{user.clubRole}{user.board ? " · Bestyrelsen" : ""}</div>
          <div style={{ fontSize: 12, color: C.soft }}>{user.sysRole === "admin" ? "Administrator" : "Medlem"}</div>
          <button onClick={() => fileRef.current?.click()} style={{ background: "none", border: "none", color: C.soft, fontSize: 12, cursor: "pointer", fontFamily: "Outfit", textDecoration: "underline", padding: 0, marginTop: 4 }}>
            Skift profilbillede
          </button>
        </div>
      </div>
      <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: C.r, overflow: "hidden", marginBottom: 16 }}>
        {[["Email", user.email||"–"],["Telefon", user.phone||"–"],["Adresse", user.address||"–"]].map(([k,v], i, arr) => (
          <div key={k} style={{ display: "flex", justifyContent: "space-between", padding: "16px 20px", borderBottom: i < arr.length-1 ? `1px solid ${C.border}` : "none", fontSize: 15 }}>
            <span style={{ color: C.soft }}>{k}</span>
            <span style={{ color: C.white }}>{v}</span>
          </div>
        ))}
      </div>
      <div style={{ display: "flex", gap: 10, flexDirection: "column" }}>
        <Btn v="ghost" style={{ justifyContent: "center" }} onClick={() => setMemberModal({ mode:"edit", member:user, isOwnProfile:true })}>✎ Redigér profil</Btn>
        <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: C.rs, padding: "14px 16px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div>
            <div style={{ fontSize: 15, color: C.white, fontWeight: 600 }}>{theme === "dark" ? "🌙 Mørkt tema" : "☀️ Lyst tema"}</div>
            <div style={{ fontSize: 12, color: C.soft, marginTop: 2 }}>Skift udseende</div>
          </div>
          <button onClick={toggleTheme} style={{
            background: theme === "dark" ? C.gold : "transparent",
            border: `2px solid ${theme === "dark" ? C.gold : C.border}`,
            borderRadius: 20, width: 52, height: 28, cursor: "pointer", position: "relative", transition: "all 0.2s", flexShrink: 0,
          }}>
            <div style={{ width: 20, height: 20, borderRadius: "50%", background: C.white, position: "absolute", top: 2, left: theme === "dark" ? 28 : 4, transition: "left 0.2s" }} />
          </button>
        </div>
        <Btn v="danger" style={{ justifyContent: "center" }} onClick={() => setUser(null)}>Afslut session</Btn>
        <div style={{ textAlign: "center", marginTop: 8 }}>
          <div style={{ fontSize: 12, color: C.faint }}>ver. 2.49 · © 2026</div>
          <div style={{ fontSize: 12, color: C.faint, marginTop: 2 }}>Made with ❤️ by Ulrik & Morten</div>
          <ChangelogSection />
        </div>
      </div>
    </div>
    );
  };

  return (
    <div style={{ height: "100vh", background: C.bg, display: "flex", flexDirection: "column", fontFamily: "Outfit", color: C.white, overflow: "hidden" }}>

      {/* ── TOP HEADER ───────────────────────────────────────────── */}
      <header style={{ background: C.surface, borderBottom: `1px solid ${C.border}`, padding: "12px 16px", display: "flex", alignItems: "center", justifyContent: "space-between", position: "sticky", top: 0, zIndex: 100 }}>
        <div>
          <div style={{ fontFamily: "Outfit", fontSize: 20, fontWeight: 900, color: C.white, lineHeight: 1 }}>
            Round <span style={{ color: C.gold }}>Table</span> <span style={{ color: C.soft, fontSize: 16 }}>30X6</span>
          </div>
        </div>
        {/* Context action for current tab */}
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {isAdmin && <SmBtn v="ghost" onClick={() => setNotifyModal(true)} style={{ fontSize: 13, padding: "7px 12px", minHeight: 36 }}>📣</SmBtn>}
          {tab === "home" && isAdmin && <Btn onClick={() => setMeetingModal({ mode:"new" })} style={{ fontSize: 14, padding: "9px 16px", minHeight: 40 }}>＋ Nyt møde</Btn>}
          {tab === "meetings" && isAdmin && <Btn onClick={() => setMeetingModal({ mode:"new" })} style={{ fontSize: 14, padding: "9px 16px", minHeight: 40 }}>＋ Nyt møde</Btn>}
          {tab === "meetings" && isBoardOrAdmin && <Btn v="ghost" onClick={() => setMeetingModal({ mode:"new", prefill:{ isBoardMeeting:true, noAttendance:true } })} style={{ fontSize: 13, padding: "9px 14px", minHeight: 40, marginLeft: 8 }}>🏛️ Bestyrelsesmøde</Btn>}
          {tab === "members" && isAdmin && <Btn onClick={() => setMemberModal({ mode:"new" })} style={{ fontSize: 14, padding: "9px 16px", minHeight: 40 }}>＋ Tilføj</Btn>}
        </div>
      </header>

      {/* ── MAIN CONTENT ─────────────────────────────────────────── */}
      <main style={{ flex: 1, overflowY: "auto", padding: "16px 16px", paddingBottom: BOTTOM_NAV_H + 16, width: "100%", boxSizing: "border-box" }}>

        {/* HOME */}
        {tab === "home" && (() => {
          const nextMeeting = upcoming[0] || null;
          const days = nextMeeting ? daysUntil(nextMeeting.date) : null;
          const totalM = members.length;
          const pct = nextMeeting ? meetingAttPct(nextMeeting, totalM) : null;
          const isAtt = nextMeeting ? nextMeeting.attendees.includes(user.id) : false;
          const isAbs = nextMeeting ? nextMeeting.absent.includes(user.id) : false;
          const unanswered = nextMeeting ? members.filter(m => !nextMeeting.attendees.includes(m.id) && !nextMeeting.absent.includes(m.id)) : [];
          const canFines = nextMeeting && (nextMeeting.boedemester === user.id || isAdmin) && nextMeeting.boedemester;

          return (
            <div>
              {/* Big welcome */}
              <div style={{ marginBottom: 32 }}>
                <div style={{ fontSize: 14, color: C.gold, fontWeight: 600, letterSpacing: "0.15em", textTransform: "uppercase", marginBottom: 8 }}>Velkommen</div>
                <h1 style={{ margin: "0 0 6px", fontFamily: "Outfit", fontSize: 44, fontWeight: 900, color: C.white, lineHeight: 1.05 }}>
                  {user.name.split(" ")[0]}
                </h1>
                {nextMeeting && (
                  <div style={{ fontSize: 20, color: C.soft, fontWeight: 400, marginTop: 8 }}>
                    Vi ses allerede om{" "}
                    <span style={{ color: C.gold, fontWeight: 700, fontFamily: "Outfit", fontSize: 24 }}>{days}</span>
                    {" "}dag{days === 1 ? "" : "e"}
                  </div>
                )}
                {/* Next birthday */}
                {(() => {
                  const nb = nextBirthday(members);
                  if (!nb) return null;
                  const bdYear  = new Date(nb.member.birthday).getFullYear();
                  const ageNext = nb.next.getFullYear() - bdYear;
                  const isToday = nb.days === 0;
                  return (
                    <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 14, background: C.surface, border: `1px solid ${C.gold}30`, borderRadius: C.rs, padding: "14px 16px" }}>
                      <span style={{ fontSize: 26 }}>🎂</span>
                      <div>
                        <div style={{ fontSize: 15, color: C.white, fontWeight: 700 }}>
                          {isToday ? "Tillykke " : "Næste: "}{nb.member.name}
                          {" "}<span style={{ color: C.gold }}>fylder {ageNext} år</span>
                        </div>
                        <div style={{ fontSize: 13, color: C.soft }}>
                          {isToday ? "🎉 Tillykke i dag!" : `${fmtBirthday(nb.member.birthday)} · om ${nb.days} dag${nb.days === 1 ? "" : "e"}`}
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </div>

              {/* Next meeting card */}
              {nextMeeting && (
                <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: C.r, overflow: "hidden" }}>
                  <div style={{ height: 4, background: `linear-gradient(90deg, ${C.gold}, transparent 70%)` }} />
                  <div style={{ padding: "22px 20px 18px" }}>
                    {nextMeeting.noAttendance && (
                      <div style={{ fontSize: 11, color: C.yellow, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 4 }}>
                        ★ Ikke mødepligt
                      </div>
                    )}
                    <div style={{ fontSize: 11, color: C.gold, fontWeight: 600, letterSpacing: "0.14em", textTransform: "uppercase", marginBottom: 4 }}>{nextMeeting.subtitle}</div>
                    <h2 style={{ margin: "0 0 14px", fontFamily: "Outfit", fontSize: 28, fontWeight: 900, color: C.white }}>{nextMeeting.title}</h2>

                    {nextMeeting.description && <DescriptionBlock text={nextMeeting.description} style={{ margin: "0 0 16px" }} />}

                    {/* Date with ICS download + location */}
                    <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 18 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 15, color: C.white }}>
                        <span style={{ color: C.gold }}>📅</span>
                        <span style={{ flex: 1 }}>{fmtLong(nextMeeting.date)} · kl. {nextMeeting.time}</span>
                        <button onClick={() => downloadICS(nextMeeting)} style={{ background: C.gold+"20", border:`1px solid ${C.gold}50`, borderRadius: C.rs, padding: "5px 12px", color: C.gold, fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "Outfit", whiteSpace: "nowrap" }}>
                          ＋ Kalender
                        </button>
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 15, color: C.white }}>
                        <span style={{ color: C.gold }}>📍</span>
                        <MapLink location={nextMeeting.location} />
                      </div>
                      {nextMeeting.deadline && (() => {
                        const dl = new Date(nextMeeting.deadline);
                        const daysLeft = Math.ceil((dl - TODAY) / 86400000);
                        const isPastDl = daysLeft < 0;
                        const isClose = daysLeft >= 0 && daysLeft <= 2;
                        const col = isPastDl ? C.red : isClose ? C.yellow : C.soft;
                        return (
                          <div style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 14 }}>
                            <span style={{ color: col }}>⏰</span>
                            <span style={{ color: col, fontWeight: isPastDl || isClose ? 600 : 400 }}>
                              {isPastDl ? `Tilmeldingsfrist udløbet (${fmtShort(nextMeeting.deadline)})`
                                : daysLeft === 0 ? "Tilmeldingsfrist: I DAG"
                                : `Tilmeldingsfrist: ${fmtShort(nextMeeting.deadline)} (${daysLeft} dage)`}
                            </span>
                          </div>
                        );
                      })()}
                    </div>

                    {/* Roles */}
                    {(nextMeeting.responsible1 || nextMeeting.boedemester || nextMeeting.treMinutter) && (
                      <div style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: C.rs, padding: "14px 16px", marginBottom: 18 }}>
                        {nextMeeting.responsible1 && <div style={{ display: "flex", justifyContent: "space-between", fontSize: 14, marginBottom: 8 }}>
                          <span style={{ color: C.soft }}>Ansvarlig</span>
                          <span style={{ color: C.white, fontWeight: 600 }}>{getMember(members, nextMeeting.responsible1)?.name}{nextMeeting.responsible2 ? " & " + getMember(members, nextMeeting.responsible2)?.name : ""}</span>
                        </div>}
                        {nextMeeting.boedemester && <div style={{ display: "flex", justifyContent: "space-between", fontSize: 14, marginBottom: nextMeeting.treMinutter ? 8 : 0 }}>
                          <span style={{ color: C.soft }}>Bødemester</span>
                          <span style={{ color: C.white, fontWeight: 600 }}>{getMember(members, nextMeeting.boedemester)?.name}</span>
                        </div>}
                        {nextMeeting.treMinutter && <div style={{ display: "flex", justifyContent: "space-between", fontSize: 14 }}>
                          <span style={{ color: C.soft }}>3 Minutter</span>
                          <span style={{ color: C.white, fontWeight: 600 }}>{getMember(members, nextMeeting.treMinutter)?.name}</span>
                        </div>}
                      </div>
                    )}

                    {/* Attendance ring + counts */}
                    <div style={{ display: "flex", alignItems: "center", gap: 18, marginBottom: 18 }}>
                      <PctRing pct={pct} size={70} stroke={6} />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 13, color: C.soft, marginBottom: 8 }}>Mødeprocent</div>
                        <div style={{ display: "flex", gap: 20 }}>
                          <div><div style={{ fontSize: 26, fontWeight: 900, fontFamily: "Outfit", color: C.green }}>{nextMeeting.attendees.length}</div><div style={{ fontSize: 11, color: C.green }}>TILMELDT</div></div>
                          <div><div style={{ fontSize: 26, fontWeight: 900, fontFamily: "Outfit", color: C.red }}>{nextMeeting.absent.length}</div><div style={{ fontSize: 11, color: C.red }}>AFBUD</div></div>
                          <div><div style={{ fontSize: 26, fontWeight: 900, fontFamily: "Outfit", color: C.soft }}>{unanswered.length}</div><div style={{ fontSize: 11, color: C.soft }}>UBESVARET</div></div>
                        </div>
                      </div>
                    </div>

                    {/* Attendee lists */}
                    <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 6 }}>
                      {[
                        ["Tilmeldte", nextMeeting.attendees, C.green],
                        ["Afbud", nextMeeting.absent, C.red],
                        ["Ikke svaret", unanswered.map(m => m.id), C.soft],
                      ].map(([lbl, ids, col]) => (
                        <div key={lbl} style={{ background: C.bg, border: `1px solid ${col}20`, borderRadius: C.rs, padding: 12 }}>
                          <div style={{ fontSize: 10, color: col, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 8 }}>{lbl} ({ids.length})</div>
                          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                            {ids.map(id => { const m = getMember(members, id); return m ? <div key={id} style={{ display: "flex", alignItems: "center", gap: 8 }}><Avatar member={m} size={28}/><span style={{ fontSize: 14, color: C.white }}>{m.name}</span></div> : null; })}
                            {ids.length === 0 && <div style={{ fontSize: 12, color: C.faint }}>Ingen</div>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Actions */}
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 8, borderTop: `1px solid ${C.border}`, padding: "14px 20px", background: C.bg + "cc" }}>
                    <SmBtn v="sub" onClick={() => setMeetingModal({ mode: "view", meeting: nextMeeting })}>Detaljer</SmBtn>
                    <SmBtn v={isAtt ? "green" : "ghost"} onClick={() => rsvp(nextMeeting.id, true)}>✓ Tilmeld</SmBtn>
                    <SmBtn v={isAbs ? "danger" : "ghost"} onClick={() => rsvp(nextMeeting.id, false)}>✕ Afmeld</SmBtn>
                    {canFines && <SmBtn v="ghost" onClick={() => setFinesModal(nextMeeting)} style={{ marginLeft: "auto", color: C.gold, borderColor: C.gold + "50" }}>⚖️ Bødeliste</SmBtn>}
                    {isAdmin && <SmBtn v="ghost" onClick={() => setMeetingModal({ mode: "edit", meeting: nextMeeting })} style={{ color: C.gold, borderColor: C.gold + "40" }}>✎ Redigér</SmBtn>}
                    <SmBtn v="ghost" onClick={() => setRsvpLogModal(nextMeeting)} style={{ color: C.soft, fontSize: 12 }}>📋 Log</SmBtn>
                  </div>
                </div>
              )}
              {!nextMeeting && <div style={{ color: C.faint, fontSize: 16 }}>Ingen kommende møder planlagt.</div>}
            </div>
          );
        })()}

        {/* MEETINGS */}
        {tab === "meetings" && (
          <div>
            <h1 style={{ margin: "0 0 28px", fontFamily: "Outfit", fontSize: 36, fontWeight: 900 }}>Alle møder</h1>
            {upcoming.length > 0 && <>
              <div style={{ fontSize: 11, fontWeight: 700, color: C.gold, letterSpacing: "0.16em", textTransform: "uppercase", marginBottom: 14 }}>Kommende</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 18, marginBottom: 36 }}>
                {upcoming.map(m => (
                  <UpcomingCard key={m.id} meeting={m} members={members} user={user} isAdmin={isAdmin}
                    onView={() => setMeetingModal({ mode: "view", meeting:m })}
                    onRSVP={a => rsvp(m.id, a)} onFines={() => setFinesModal(m)}
                    onRsvpLog={() => setRsvpLogModal(m)} />
                ))}
              </div>
            </>}
            {archive.length > 0 && <>
              <div style={{ fontSize: 11, fontWeight: 700, color: C.soft, letterSpacing: "0.16em", textTransform: "uppercase", marginBottom: 14 }}>Arkiv</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {archive.map(m => <ArchiveRow key={m.id} meeting={m} members={members} user={user} isAdmin={isAdmin}
                  onClick={() => setMeetingModal({ mode:"view", meeting:m })}
                  onFines={() => setFinesModal(m)} />)}
              </div>
            </>}
          </div>
        )}

        {/* MEMBERS */}
        {tab === "members" && (
          <div>
            <div style={{ marginBottom: 28 }}>
              <h1 style={{ margin: "0 0 4px", fontFamily: "Outfit", fontSize: 36, fontWeight: 900 }}>Medlemmer</h1>
              <div style={{ fontSize: 14, color: C.soft }}>{members.length} registrerede</div>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              {members.map(m => (
                <MemberCard key={m.id} member={m} meetings={meetings} isAdmin={isAdmin}
                  onEdit={() => setMemberModal({ mode:"edit", member:m })}
                  onDelete={() => deleteMember(m.id)}
                  onResetPw={() => setChangePwModal(m)} />
              ))}
            </div>
          </div>
        )}

        {/* STATS */}
        {tab === "stats" && <StatsTab members={members} meetings={meetings} />}

        {/* CHAT */}
        {tab === "chat" && (
          <ChatTab user={user} members={members} messages={messages} notifications={notifications}
            unreadChat={unreadChat} unreadNotifs={unreadNotifs} onSend={sendMessage}
            onClearChatBadge={() => { setUnreadChat(0); localStorage.setItem("chat-last-seen", new Date().toISOString()); }}
            onClearNotifBadge={() => { setUnreadNotifs(0); localStorage.setItem("notif-tab-opened-ts", new Date().toISOString()); }} />
        )}

        {/* PROFILE */}
        {tab === "profile" && <ProfileView />}
      </main>

      {/* ── BOTTOM NAVIGATION ────────────────────────────────────── */}
      <nav style={{
        position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 200,
        background: C.surface,
        borderTop: `1px solid ${C.border}`,
        display: "flex", alignItems: "flex-start",
        height: `calc(${BOTTOM_NAV_H}px + env(safe-area-inset-bottom, 20px))`,
        paddingBottom: "env(safe-area-inset-bottom, 20px)",
        paddingTop: 10,
      }}>
        {NAV.map(n => {
          const active = tab === n.id;
          return (
            <button key={n.id} onClick={() => handleTabClick(n.id)} style={{
              flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
              gap: 4, background: "none", border: "none", cursor: "pointer",
              padding: "8px 4px", transition: "opacity 0.15s", position: "relative",
              userSelect: "none", WebkitUserSelect: "none", touchAction: "manipulation",
            }}>
              <div style={{
                width: 40, height: 40, borderRadius: 12,
                background: active ? C.gold + "22" : "transparent",
                display: "flex", alignItems: "center", justifyContent: "center",
                transition: "background 0.15s", fontSize: 20, position: "relative",
              }}>
                {n.emoji}
                {n.badge > 0 && (
                  <div style={{
                    position: "absolute", top: -4, right: -4,
                    background: C.red, color: "#fff", borderRadius: "50%",
                    width: 18, height: 18, fontSize: 11, fontWeight: 700,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontFamily: "Outfit", border: `2px solid ${C.surface}`,
                  }}>{n.badge > 9 ? "9+" : n.badge}</div>
                )}
              </div>
              <span style={{
                fontSize: 10, fontWeight: active ? 700 : 400,
                color: active ? C.gold : C.soft,
                letterSpacing: "0.04em", fontFamily: "Outfit",
                whiteSpace: "nowrap",
              }}>{n.label}</span>
              {active && <div style={{ width: 4, height: 4, borderRadius: "50%", background: C.gold, position: "absolute", bottom: 8 }} />}
            </button>
          );
        })}
      </nav>

      {/* ── MODALS ───────────────────────────────────────────────── */}
      {notifyModal && (
        <NotifyModal meetings={meetings} onClose={() => setNotifyModal(false)} onSend={sendPushToAll} />
      )}
      {meetingModal && (
        <MeetingModal mode={meetingModal.mode}
          meeting={meetings.find(m => m.id === meetingModal.meeting?.id) || meetingModal.meeting}
          members={members} isAdmin={isAdmin} user={user}
          prefill={meetingModal.prefill}
          onClose={() => setMeetingModal(null)} onSave={saveMeeting}
          onDelete={deleteMeeting} onDocAdd={addDocToMeeting}
          onAdminRsvp={adminRsvp} />
      )}
      {finesModal && (
        <FinesModal meeting={finesModal} members={members} isAdmin={isAdmin} user={user}
          onClose={() => setFinesModal(null)} onSave={fines => saveFines(finesModal.id, fines)} />
      )}
      {rsvpLogModal && (
        <RsvpLogModal meeting={rsvpLogModal} members={members} onClose={() => setRsvpLogModal(null)} />
      )}
      {changePwModal && (
        <ChangePasswordModal member={changePwModal} onClose={() => setChangePwModal(null)}
          onSave={adminResetPassword} />
      )}
      {memberModal && (
        <MemberModal mode={memberModal.mode} member={memberModal.member} onClose={() => setMemberModal(null)}
          onSave={saveMember} isOwnProfile={memberModal.isOwnProfile} />
      )}
    </div>
  );
}
